import * as THREE from 'three';
import collisionReleases from '../../shared/mapCollisionReleases.json' with {type:'json'};
import { applyMapDocument } from '../../shared/mapStudio.js';
import {
  roundedBox, box, cylinder, sphere, plane, toon, standard, seededRandom,
  plankTexture, speckleTexture, leaf, irregularRock, tubeBetween,
} from './toon.js';

const MAP_SIZE_MULTIPLIER = 2;

const addAt = (group, object, x, y, z, rotationY = 0) => {
  object.position.set(x, y, z);
  object.rotation.y = rotationY;
  group.add(object);
  return object;
};

function addObstacle(world, x, z, width, depth, tag = 'prop', minY = 0, maxY = 10) {
  world.obstacles.push({
    shape: 'box',
    minX: x - width / 2,
    maxX: x + width / 2,
    minZ: z - depth / 2,
    maxZ: z + depth / 2,
    minY,
    maxY,
    tag,
  });
}

function addCircleObstacle(world, x, z, radius, tag = 'prop', minY = 0, maxY = 10) {
  world.obstacles.push({
    shape: 'circle',
    x,
    z,
    radius,
    minX: x - radius,
    maxX: x + radius,
    minZ: z - radius,
    maxZ: z + radius,
    minY,
    maxY,
    tag,
  });
}

function pointNearObstacle(world, x, z, margin = 0) {
  return world.obstacles.some((obstacle)=>
    x>=obstacle.minX-margin&&x<=obstacle.maxX+margin&&z>=obstacle.minZ-margin&&z<=obstacle.maxZ+margin
  );
}

function addLocalObstacle(world, originX, originZ, localX, localZ, width, depth, rotationY, tag, minY = 0, maxY = 10) {
  const cosine = Math.cos(rotationY);
  const sine = Math.sin(rotationY);
  const x = originX + localX * cosine + localZ * sine;
  const z = originZ - localX * sine + localZ * cosine;
  const rotatedWidth = Math.abs(cosine) * width + Math.abs(sine) * depth;
  const rotatedDepth = Math.abs(sine) * width + Math.abs(cosine) * depth;
  addObstacle(world, x, z, rotatedWidth, rotatedDepth, tag, minY, maxY);
}

function addWallArt(group, x, y, z, width, height, color, onSide = false) {
  const frame = roundedBox(width + .35, height + .35, .18, '#5b3b2e', .06, 2);
  const art = roundedBox(width, height, .12, color, .04, 2);
  art.position.z = -.12;
  const sun = sphere(Math.min(width, height) * .15, '#ffd64e', 12, 8);
  sun.scale.z = .16;
  sun.position.set(-width * .23, height * .18, -.21);
  frame.add(art, sun);
  frame.position.set(x, y, z);
  if (onSide) frame.rotation.y = Math.PI / 2;
  group.add(frame);
}

function addPlant(group, x, z, scale = 1) {
  const plant = new THREE.Group();
  const pot = cylinder(.6 * scale, .44 * scale, .86 * scale, '#da704c', 12);
  pot.position.y = .43 * scale;
  const potRim = new THREE.Mesh(new THREE.TorusGeometry(.56 * scale, .095 * scale, 7, 14), toon('#ee8a5d'));
  potRim.rotation.x = Math.PI / 2;
  potRim.position.y = .85 * scale;
  const soil = cylinder(.48 * scale, .48 * scale, .08 * scale, '#493426', 14);
  soil.position.y = .87 * scale;
  plant.add(pot, potRim, soil);
  const stem = cylinder(.09 * scale, .12 * scale, 1.8 * scale, '#358354', 8);
  stem.position.y = 1.58 * scale;
  plant.add(stem);
  for (let i = 0; i < 7; i += 1) {
    const plantLeaf = leaf(.65 * scale, 1.05 * scale, i % 2 ? '#49a85f' : '#2f8d52', .04 * scale);
    const angle = i * 2.4;
    plantLeaf.rotation.set(.25 + i * .04, angle, angle * .1);
    plantLeaf.position.set(Math.sin(angle) * .42 * scale, (1.25 + i * .2) * scale, Math.cos(angle) * .42 * scale);
    plant.add(plantLeaf);
  }
  addAt(group, plant, x, 0, z);
  return plant;
}

function createFloor(width, depth, colorA, colorB, repeats = [8, 6]) {
  const texture = plankTexture(colorA, colorB);
  texture.repeat.set(...repeats);
  const material = standard('#ffffff', { roughness: .92 });
  material.map = texture;
  material.needsUpdate = true;
  const floor = new THREE.Mesh(new THREE.BoxGeometry(width, .35, depth), material);
  floor.position.y = -.2;
  floor.receiveShadow = true;
  return floor;
}

function addRoomShell(world, width, depth, wallColor, floorColors) {
  world.group.add(createFloor(width, depth, floorColors[0], floorColors[1], [Math.round(width/4.4),Math.round(depth/4.3)]));
  const back = box(width, 11, .45, wallColor);
  back.position.set(0, 5.3, -depth / 2);
  const left = box(.45, 11, depth, wallColor);
  left.position.set(-width / 2, 5.3, 0);
  const right = left.clone();
  right.position.x = width / 2;
  // La pared frontal es una cara simple orientada hacia la habitación. Desde
  // fuera no se dibuja, por lo que la cámara puede acercarse al límite sin que
  // una pared completa tape al auto.
  const frontMaterial=standard(wallColor,{roughness:.94});
  const front=new THREE.Mesh(new THREE.PlaneGeometry(width,11),frontMaterial);
  front.position.set(0,5.3,depth/2-.02);
  front.rotation.y=Math.PI;
  front.receiveShadow=true;
  front.userData.noShadow=true;
  world.group.add(back, left, right, front);
  const baseColor = '#f7efe1';
  const backBase = roundedBox(width, .55, .32, baseColor, .08, 2);
  backBase.position.set(0, .28, -depth / 2 + .25);
  const sideBase = roundedBox(.32, .55, depth, baseColor, .08, 2);
  sideBase.position.set(-width / 2 + .25, .28, 0);
  const sideBase2 = sideBase.clone();
  sideBase2.position.x = width / 2 - .25;
  const frontBaseMaterial=standard(baseColor,{roughness:.9});
  const frontBase=new THREE.Mesh(new THREE.PlaneGeometry(width,.55),frontBaseMaterial);
  frontBase.position.set(0,.28,depth/2-.04);
  frontBase.rotation.y=Math.PI;
  frontBase.receiveShadow=true;
  frontBase.userData.noShadow=true;
  world.group.add(backBase, sideBase, sideBase2, frontBase);
}

function addSectorRug(group, x, z, width, depth, color, borderColor) {
  const rug = plane(width, depth, color);
  rug.position.set(x, .012, z);
  group.add(rug);
  const borderThickness=.24;
  const borderHeight=.024;
  [[width,borderThickness,0,-depth/2+borderThickness/2],[width,borderThickness,0,depth/2-borderThickness/2]].forEach(([w,d,ox,oz])=>{
    const border=roundedBox(w,borderHeight,d,borderColor,.01,1);border.position.set(x+ox,.038,z+oz);group.add(border);
  });
  [[borderThickness,depth-borderThickness*2,-width/2+borderThickness/2,0],[borderThickness,depth-borderThickness*2,width/2-borderThickness/2,0]].forEach(([w,d,ox,oz])=>{
    const border=roundedBox(w,borderHeight,d,borderColor,.01,1);border.position.set(x+ox,.038,z+oz);group.add(border);
  });
  return rug;
}

function addKitchenArea(world) {
  const kitchenX=22;
  const backZ=-world.depth/2+2.2;
  const tile=plane(31,23,'#eadfc9');
  tile.position.set(24,.014,-18.2);
  world.group.add(tile);
  for(let x=10;x<=38;x+=4){
    const grout=roundedBox(.055,.018,22.5,'#c9bba4',.006,1);grout.position.set(x,.034,-18.2);world.group.add(grout);
  }
  for(let z=-28;z<=-7;z+=4){
    const grout=roundedBox(30.5,.018,.055,'#c9bba4',.006,1);grout.position.set(24,.035,z);world.group.add(grout);
  }

  const counter=new THREE.Group();
  const cabinet=roundedBox(20.5,3.2,3.45,'#79a884',.2,4);cabinet.position.y=1.6;
  const counterTop=roundedBox(21.1,.42,3.9,'#f2ddbd',.15,3);counterTop.position.y=3.35;
  counter.add(cabinet,counterTop);
  [-8.1,-4.05,0,4.05,8.1].forEach((x,index)=>{
    const door=roundedBox(3.55,2.48,.08,index===2?'#6e9d7a':'#82b18d',.1,3);door.position.set(x,1.62,1.77);counter.add(door);
    const knob=sphere(.09,'#f1c75d',10,7);knob.position.set(x+.92,1.62,1.84);counter.add(knob);
  });
  addAt(world.group,counter,kitchenX,0,backZ);
  addObstacle(world,kitchenX,backZ,21.5,4.1,'kitchen-counter',0,3.65);

  const sink=roundedBox(4.2,.16,2.1,'#b9c8cc',.18,3);sink.position.set(kitchenX-4.5,3.62,backZ+.15);world.group.add(sink);
  const sinkBasin=roundedBox(3.35,.07,1.42,'#6f969e',.16,3);sinkBasin.position.set(kitchenX-4.5,3.72,backZ+.15);world.group.add(sinkBasin);
  const faucetStem=cylinder(.12,.12,1.25,'#c8d1d2',10);faucetStem.position.set(kitchenX-4.5,4.23,backZ-.72);world.group.add(faucetStem);
  const faucetArc=new THREE.Mesh(new THREE.TorusGeometry(.55,.11,7,14,Math.PI),toon('#c8d1d2'));faucetArc.rotation.x=Math.PI/2;faucetArc.position.set(kitchenX-4.5,4.82,backZ-.2);world.group.add(faucetArc);

  const cooktop=roundedBox(5.1,.14,2.35,'#30323a',.13,3);cooktop.position.set(kitchenX+5.8,3.65,backZ+.05);world.group.add(cooktop);
  [[-1.5,-.62],[-1.5,.62],[1.5,-.62],[1.5,.62]].forEach(([x,z])=>{
    const burner=new THREE.Mesh(new THREE.TorusGeometry(.46,.07,7,15),toon('#798087'));burner.rotation.x=Math.PI/2;burner.position.set(kitchenX+5.8+x,3.77,backZ+.05+z);world.group.add(burner);
  });
  const hood=roundedBox(6.1,1.2,2.5,'#d3dad8',.2,4);hood.position.set(kitchenX+5.8,8.35,backZ-.15);world.group.add(hood);
  const hoodChimney=roundedBox(2.8,3.7,1.7,'#b8c3c1',.12,3);hoodChimney.position.set(kitchenX+5.8,10.45,backZ-.25);world.group.add(hoodChimney);

  const fridgeX=36.1;
  const fridgeZ=-world.depth/2+3;
  const fridge=roundedBox(6.1,9.4,4.7,'#dce8e7',.28,5);fridge.position.set(fridgeX,4.7,fridgeZ);world.group.add(fridge);
  const fridgeLine=roundedBox(5.5,.12,.08,'#a9bcbc',.04,2);fridgeLine.position.set(fridgeX,3.75,fridgeZ+2.39);world.group.add(fridgeLine);
  const freezerHandle=roundedBox(.16,2.45,.15,'#879a9d',.05,2);freezerHandle.position.set(fridgeX+2.15,2.1,fridgeZ+2.49);world.group.add(freezerHandle);
  const fridgeHandle=roundedBox(.16,3.75,.15,'#879a9d',.05,2);fridgeHandle.position.set(fridgeX+2.15,6.4,fridgeZ+2.49);world.group.add(fridgeHandle);
  addObstacle(world,fridgeX,fridgeZ,6.5,5.1,'fridge',0,9.5);

  const islandX=23;
  const islandZ=-12.2;
  const islandBase=roundedBox(11.5,3.0,4.5,'#d88a55',.22,4);islandBase.position.set(islandX,1.5,islandZ);world.group.add(islandBase);
  const islandTop=roundedBox(12.2,.42,5.15,'#f1d4a8',.18,3);islandTop.position.set(islandX,3.2,islandZ);world.group.add(islandTop);
  [-3.7,0,3.7].forEach((x)=>{
    const islandDoor=roundedBox(3.15,2.25,.08,'#e29a65',.11,3);islandDoor.position.set(islandX+x,1.55,islandZ+2.3);world.group.add(islandDoor);
    const handle=roundedBox(.8,.11,.11,'#6d5343',.04,2);handle.position.set(islandX+x,2.15,islandZ+2.4);world.group.add(handle);
  });
  addObstacle(world,islandX,islandZ,12.5,5.45,'kitchen-island',0,3.45);

  [-3.25,3.25].forEach((x)=>{
    const stool=new THREE.Group();
    const seat=cylinder(1.05,1.05,.38,'#55a4b8',14);seat.position.y=2.35;stool.add(seat);
    [-.55,.55].forEach((sx)=>[-.45,.45].forEach((sz)=>{const leg=roundedBox(.18,2.25,.18,'#6d5140',.05,2);leg.position.set(sx,1.12,sz);stool.add(leg);}));
    addAt(world.group,stool,islandX+x,0,islandZ+4.4);
    addObstacle(world,islandX+x,islandZ+4.4,2.25,2.25,'kitchen-stool-seat',2.12,2.62);
    [-.55,.55].forEach((sx)=>[-.45,.45].forEach((sz)=>addObstacle(world,islandX+x+sx,islandZ+4.4+sz,.38,.38,'kitchen-stool-leg',0,2.3)));
  });

  const bowl=cylinder(1.25,.92,.38,'#4f87a0',16);bowl.position.set(islandX,3.55,islandZ);world.group.add(bowl);
  [['#e65d4e',-.48,0],['#f2c64d',.42,.14],['#66a95b',0,-.38]].forEach(([color,x,z])=>{
    const fruit=sphere(.42,color,12,8);fruit.position.set(islandX+x,4,islandZ+z);world.group.add(fruit);
  });
}

function addDiningAreaDetails(world) {
  const hutchX=-world.width/2+1.75;
  const hutchZ=12;
  const hutch=new THREE.Group();
  const lower=roundedBox(10.5,3.1,2.85,'#76503e',.2,4);lower.position.y=1.55;hutch.add(lower);
  const lowerTop=roundedBox(10.9,.36,3.15,'#a36c49',.13,3);lowerTop.position.y=3.22;hutch.add(lowerTop);
  [-3.45,0,3.45].forEach((x,index)=>{
    const door=roundedBox(2.85,2.35,.08,index===1?'#845947':'#8d6049',.1,3);door.position.set(x,1.62,1.47);hutch.add(door);
    const knob=sphere(.09,'#f0ca62',10,7);knob.position.set(x+.58,1.62,1.56);hutch.add(knob);
  });
  const upperBack=roundedBox(9.7,4.6,.58,'#8f6249',.18,4);upperBack.position.set(0,5.65,-.88);hutch.add(upperBack);
  [-2.5,0,2.5].forEach((x)=>{
    const panel=roundedBox(2.05,3.55,.1,'#8db8bb',.12,3);panel.position.set(x,5.72,-.53);hutch.add(panel);
    const plate=new THREE.Mesh(new THREE.TorusGeometry(.62,.1,7,16),toon('#f7e6bc'));plate.position.set(x,5.72,-.42);hutch.add(plate);
  });
  [4.1,6.25,7.85].forEach((y)=>{const shelf=roundedBox(9.8,.22,1.35,'#ba7e54',.07,2);shelf.position.set(0,y,-.25);hutch.add(shelf);});
  const crown=roundedBox(10.4,.5,1.55,'#6d493a',.12,3);crown.position.set(0,8.25,-.3);hutch.add(crown);
  addAt(world.group,hutch,hutchX,0,hutchZ,Math.PI/2);
  addLocalObstacle(world,hutchX,hutchZ,0,0,11,3.3,Math.PI/2,'dining-hutch',0,8.55);

  const buffetX=-16;
  const buffetZ=27.2;
  const buffet=new THREE.Group();
  const buffetBody=roundedBox(10,3.2,2.7,'#537c70',.2,4);buffetBody.position.y=1.6;buffet.add(buffetBody);
  const buffetTop=roundedBox(10.5,.34,3.05,'#e3b66b',.12,3);buffetTop.position.y=3.35;buffet.add(buffetTop);
  [-3.25,0,3.25].forEach((x,index)=>{
    const cabinet=roundedBox(2.75,2.4,.08,index===1?'#609080':'#5a8578',.1,3);cabinet.position.set(x,1.62,1.39);buffet.add(cabinet);
    const pull=roundedBox(.7,.1,.1,'#f0ca62',.04,2);pull.position.set(x,2.05,1.49);buffet.add(pull);
  });
  const servingTray=roundedBox(4.2,.14,1.55,'#ca884e',.07,2);servingTray.position.set(0,3.62,0);buffet.add(servingTray);
  [-1.2,0,1.2].forEach((x,index)=>{const glass=cylinder(.3,.45,.75,index===1?'#74b5ba':'#92c7c8',12);glass.position.set(x,4.02,0);buffet.add(glass);});
  addAt(world.group,buffet,buffetX,0,buffetZ,Math.PI);
  addLocalObstacle(world,buffetX,buffetZ,0,0,10.5,3.15,Math.PI,'dining-buffet',0,3.6);

  const pendant=new THREE.Group();
  const cord=cylinder(.07,.07,3.4,'#40342e',7);cord.position.y=9.3;pendant.add(cord);
  const shade=new THREE.Mesh(new THREE.CylinderGeometry(1.15,2.1,1.45,16),toon('#e99b51'));shade.position.y=7.35;pendant.add(shade);
  const bulb=sphere(.42,'#ffe39a',12,8);bulb.position.y=6.92;pendant.add(bulb);
  addAt(world.group,pendant,-25.2,0,8.5);

  [[-35,24,1.1],[-7.5,25.5,.92]].forEach(([x,z,scale])=>{
    addPlant(world.group,x,z,scale);
    addCircleObstacle(world,x,z,.8*scale,'dining-plant',0,1.05*scale);
  });
}

function createReadingChair(color,accent) {
  const chair=new THREE.Group();
  const base=roundedBox(4.4,1.25,4.1,color,.32,5);base.position.y=.64;chair.add(base);
  const seat=roundedBox(3.35,.65,2.8,accent,.25,4);seat.position.set(0,1.48,-.25);chair.add(seat);
  const back=roundedBox(4.15,3.75,.85,color,.3,5);back.position.set(0,3.05,1.55);back.rotation.x=-.08;chair.add(back);
  [-1.85,1.85].forEach((x)=>{const arm=roundedBox(.72,1.8,3.65,color,.25,4);arm.position.set(x,1.65,.05);chair.add(arm);});
  const backCushion=roundedBox(3.3,2.65,.38,accent,.22,4);backCushion.position.set(0,3.1,1.05);backCushion.rotation.x=-.08;chair.add(backCushion);
  return chair;
}

function createBookcase(color,accent) {
  const bookcase=new THREE.Group();
  const back=roundedBox(10,8.4,2.5,color,.24,5);back.position.y=4.2;bookcase.add(back);
  [1.1,3.25,5.4,7.55].forEach((y)=>{const shelf=roundedBox(9.65,.27,2.85,accent,.08,3);shelf.position.y=y;bookcase.add(shelf);});
  [-4.55,4.55].forEach((x)=>{const side=roundedBox(.38,8.15,2.82,accent,.1,3);side.position.set(x,4.2,0);bookcase.add(side);});
  const colors=['#e45f62','#f1c34c','#4c91bd','#7c67bd','#55a574','#e7884f'];
  for(let row=0;row<3;row+=1){
    let x=-3.95;
    for(let i=0;i<7;i+=1){
      const width=.58+(i%3)*.13;
      const height=1.35+((i+row)%4)*.16;
      const book=roundedBox(width,height,.52,colors[(i+row*2)%colors.length],.06,2);book.position.set(x+width/2,1.22+row*2.15+height/2,.98);book.rotation.z=(i%2?.025:-.025);bookcase.add(book);
      x+=width+.25;
    }
  }
  return bookcase;
}

function addReadingArea(world) {
  [[7,'#5c6d92','#e5b85d'],[19,'#7c5d84','#d7975f']].forEach(([z,color,accent])=>{
    const bookcase=createBookcase(color,accent);
    const x=world.width/2-1.65;
    addAt(world.group,bookcase,x,0,z,-Math.PI/2);
    addLocalObstacle(world,x,z,0,0,10.4,3.05,-Math.PI/2,'reading-bookcase',0,8.65);
  });

  const leftChair=createReadingChair('#4f8791','#6eabb0');
  addAt(world.group,leftChair,16,0,13,-Math.PI/2);
  addLocalObstacle(world,16,13,0,0,4.9,4.55,-Math.PI/2,'reading-chair',0,5.05);
  const rightChair=createReadingChair('#b35d69','#db7880');
  addAt(world.group,rightChair,30,0,13,Math.PI/2);
  addLocalObstacle(world,30,13,0,0,4.9,4.55,Math.PI/2,'reading-chair',0,5.05);

  const tableX=23;
  const tableZ=13;
  const tableTop=cylinder(2.25,2.25,.42,'#d6a35e',18);tableTop.position.set(tableX,2.3,tableZ);world.group.add(tableTop);
  const tableStem=cylinder(.38,.52,2.15,'#73503b',12);tableStem.position.set(tableX,1.08,tableZ);world.group.add(tableStem);
  const tableFoot=cylinder(1.15,1.4,.3,'#73503b',14);tableFoot.position.set(tableX,.15,tableZ);world.group.add(tableFoot);
  addCircleObstacle(world,tableX,tableZ,.72,'reading-table-stem',0,2.18);
  addCircleObstacle(world,tableX,tableZ,2.4,'reading-table-top',2.05,2.58);
  const openBookLeft=roundedBox(1.7,.09,2.1,'#f7edcf',.06,2);openBookLeft.position.set(tableX-.82,2.58,tableZ);openBookLeft.rotation.y=.12;world.group.add(openBookLeft);
  const openBookRight=openBookLeft.clone();openBookRight.position.x=tableX+.82;openBookRight.rotation.y=-.12;world.group.add(openBookRight);
  [-.82,.82].forEach((x)=>{const line=roundedBox(1.2,.025,.045,'#9d7a61',.01,1);line.position.set(tableX+x,2.66,tableZ);world.group.add(line);});

  const lampX=32.8;
  const lampZ=25;
  const lampBase=cylinder(.85,1.05,.28,'#514942',14);lampBase.position.set(lampX,.14,lampZ);world.group.add(lampBase);
  const lampPole=cylinder(.11,.13,5.7,'#5f554d',9);lampPole.position.set(lampX,2.95,lampZ);world.group.add(lampPole);
  const lampShade=new THREE.Mesh(new THREE.CylinderGeometry(.8,1.45,1.6,15),toon('#efc55c'));lampShade.position.set(lampX,6.05,lampZ);world.group.add(lampShade);
  addCircleObstacle(world,lampX,lampZ,1.08,'reading-lamp',0,.35);

  addPlant(world.group,11.5,25,.95);
  addCircleObstacle(world,11.5,25,.8,'reading-plant',0,1.05);
  const bookStackX=24;
  const bookStackZ=23.5;
  [['#e86862',0,0],['#4e8eb8',.14,.48],['#e8c34d',-.08,.96]].forEach(([color,x,y],index)=>{
    const book=roundedBox(3.4,.42,2.1,color,.1,3);book.position.set(bookStackX+x,.22+y,bookStackZ);book.rotation.y=(index-1)*.08;world.group.add(book);
  });
  addObstacle(world,bookStackX,bookStackZ,3.8,2.5,'reading-book-stack',0,1.55);
}

function createInteriorDoor(color='#5f86a0',accent='#f0c65d',withGlass=false) {
  const door=new THREE.Group();
  const frame=roundedBox(6.2,8.8,.34,'#f5ead8',.14,3);frame.position.y=4.4;door.add(frame);
  const slab=roundedBox(5.35,8.0,.18,color,.16,4);slab.position.set(0,4.05,.22);door.add(slab);
  if(withGlass){
    const glass=roundedBox(4.35,3.65,.08,'#79b8c6',.12,3);glass.position.set(0,5.55,.34);door.add(glass);
    const crossH=roundedBox(4.05,.16,.08,'#f4ead7',.04,2);crossH.position.set(0,5.55,.4);door.add(crossH);
    const crossV=roundedBox(.16,3.35,.08,'#f4ead7',.04,2);crossV.position.set(0,5.55,.4);door.add(crossV);
    const lower=roundedBox(4.35,2.55,.08,'#4d7790',.13,3);lower.position.set(0,2.2,.34);door.add(lower);
  }else{
    [2.2,5.75].forEach((y)=>{const panel=roundedBox(4.25,2.35,.08,'#6e95aa',.13,3);panel.position.set(0,y,.34);door.add(panel);});
  }
  const knob=sphere(.18,accent,10,7);knob.position.set(1.85,4.05,.45);door.add(knob);
  return door;
}

function createRoomWindow(width=8,height=4.2,curtains=false) {
  const windowGroup=new THREE.Group();
  const frame=roundedBox(width+.55,height+.55,.3,'#fff3dc',.12,3);windowGroup.add(frame);
  const glass=roundedBox(width,height,.11,'#78b8c8',.1,3);glass.position.z=.2;windowGroup.add(glass);
  const vertical=roundedBox(.18,height-.2,.08,'#f4ead7',.04,2);vertical.position.z=.3;windowGroup.add(vertical);
  const horizontal=roundedBox(width-.2,.18,.08,'#f4ead7',.04,2);horizontal.position.z=.3;windowGroup.add(horizontal);
  const glint=roundedBox(width*.3,.07,.04,'#d5f1ef',.02,1);glint.position.set(-width*.2,height*.2,.36);glint.rotation.z=-.32;windowGroup.add(glint);
  const sill=roundedBox(width+1,.28,.78,'#cda77e',.08,2);sill.position.set(0,-height/2-.35,.08);windowGroup.add(sill);
  if(curtains){
    [-1,1].forEach((side)=>{const curtain=roundedBox(width*.18,height+.75,.22,side>0?'#e88768':'#df735f',.14,3);curtain.position.set(side*(width/2+.25-width*.09),0,.42);windowGroup.add(curtain);});
    const rod=roundedBox(width+1.1,.16,.16,'#624b3d',.05,2);rod.position.set(0,height/2+.62,.45);windowGroup.add(rod);
  }
  return windowGroup;
}

function createFrenchDoors() {
  const doors=new THREE.Group();
  const frame=roundedBox(11.4,8.9,.34,'#fff0da',.14,3);frame.position.y=4.45;doors.add(frame);
  [-2.72,2.72].forEach((x,index)=>{
    const glass=roundedBox(5.05,8.05,.12,index?'#72adbf':'#7bb8c7',.12,3);glass.position.set(x,4.35,.23);doors.add(glass);
    [-1.65,0,1.65].forEach((offset)=>{const bar=roundedBox(4.65,.16,.08,'#f4e8d6',.04,2);bar.position.set(x,4.35+offset,.34);doors.add(bar);});
    const vertical=roundedBox(.18,7.65,.08,'#f4e8d6',.04,2);vertical.position.set(x,4.35,.34);doors.add(vertical);
    const handle=roundedBox(.16,1.5,.12,'#665d55',.05,2);handle.position.set(index?-.48:.48,4.1,.42);doors.add(handle);
  });
  const center=roundedBox(.28,8.35,.16,'#e5d6c3',.06,2);center.position.set(0,4.35,.32);doors.add(center);
  return doors;
}

function addPictureSet(group,x,y,z,rotationY,colors,scale=1) {
  const root=new THREE.Group();
  [[-1.65,.7,2.2,2.8],[.95,1.0,2.6,2.0],[-.1,-1.05,2.25,1.65]].forEach(([px,py,width,height],index)=>{
    const frame=roundedBox(width*scale+.28,height*scale+.28,.18,'#5e4034',.05,2);
    const art=roundedBox(width*scale,height*scale,.1,colors[index%colors.length],.04,2);art.position.z=.13;
    const shape=sphere(Math.min(width,height)*scale*.14,index%2?'#f4cb55':'#f6e8c9',10,7);shape.scale.z=.16;shape.position.set(-width*scale*.2,height*scale*.15,.22);
    frame.add(art,shape);frame.position.set(px*scale,py*scale,0);root.add(frame);
  });
  root.position.set(x,y,z);root.rotation.y=rotationY;group.add(root);
}

function addLivingWallDetails(world) {
  const backZ=-world.depth/2+.3;
  const frontZ=world.depth/2-.3;
  const leftX=-world.width/2+.3;
  const rightX=world.width/2-.3;
  addAt(world.group,createInteriorDoor('#668ba1','#f0c65d'),2,0,backZ);
  addAt(world.group,createRoomWindow(7.6,3.8,true),17.3,7.05,backZ+.02);
  addAt(world.group,createFrenchDoors(),leftX,0,-1,Math.PI/2);
  addAt(world.group,createInteriorDoor('#5b917d','#f1c95a',true),rightX,0,-18,-Math.PI/2);
  [7,19].forEach((z)=>addAt(world.group,createRoomWindow(7.2,2.25),rightX,9.1,z,-Math.PI/2));
  addPictureSet(world.group,8.2,7.2,backZ+.05,0,['#df6d61','#5c9d76','#6574b5'],.75);
  addPictureSet(world.group,leftX+.03,7.1,-18,Math.PI/2,['#ed9a57','#527f9d','#7c66a9'],.72);
  addPictureSet(world.group,rightX-.03,6.8,-5,-Math.PI/2,['#62a08b','#e07a66','#e6bd55'],.62);
  addAt(world.group,createRoomWindow(10.5,4.1,true),-22,6.55,frontZ,Math.PI);
  addAt(world.group,createFrenchDoors(),0,0,frontZ,Math.PI);
  addAt(world.group,createInteriorDoor('#9b6a4c','#f0c65d',true),23,0,frontZ,Math.PI);
  addPictureSet(world.group,34,7.1,frontZ-.03,Math.PI,['#5f9c79','#e27d62','#e8c252'],.68);
  addPictureSet(world.group,-35,7.15,frontZ-.03,Math.PI,['#6289b4','#e89a55','#8a69a9'],.6);
}

function addBedroomWallDetails(world) {
  const backZ=-world.depth/2+.3;
  const frontZ=world.depth/2-.3;
  const leftX=-world.width/2+.3;
  const rightX=world.width/2-.3;
  addAt(world.group,createFrenchDoors(),leftX,0,22,Math.PI/2);
  addAt(world.group,createRoomWindow(8.2,4.1,true),leftX,6.25,0,Math.PI/2);
  addAt(world.group,createInteriorDoor('#7566a9','#f2c751',true),rightX,0,24,-Math.PI/2);
  addAt(world.group,createRoomWindow(7,2.35),-30,8.75,backZ+.02);
  addAt(world.group,createRoomWindow(6.5,2.15),26,9.05,backZ+.02);
  [-8,9.5].forEach((z)=>addAt(world.group,createRoomWindow(6.6,2.05),rightX,9.2,z,-Math.PI/2));
  addPictureSet(world.group,-17,7.1,backZ+.05,0,['#ef8468','#5b99b4','#f0c454'],.7);
  addPictureSet(world.group,leftX+.03,6.9,-10,Math.PI/2,['#5c82ba','#e46d77','#77a862'],.68);
  addPictureSet(world.group,rightX-.03,7.1,15,-Math.PI/2,['#7a65b1','#f0a253','#54a2aa'],.62);
  addAt(world.group,createRoomWindow(9.2,4.15,true),-24,6.55,frontZ,Math.PI);
  addAt(world.group,createFrenchDoors(),0,0,frontZ,Math.PI);
  addAt(world.group,createInteriorDoor('#e16f68','#f4d05f',true),24,0,frontZ,Math.PI);
  addPictureSet(world.group,34,7.1,frontZ-.03,Math.PI,['#4f93b8','#ef8b61','#e7c04f'],.62);
  addPictureSet(world.group,-35,7.1,frontZ-.03,Math.PI,['#7c68b5','#63a778','#ef796c'],.58);
}

function addKidsTrampoline(world,x,z) {
  addSectorRug(world.group,x,z,27,24,'#61a9af','#f2cb55');
  const trampoline=new THREE.Group();
  const mat=cylinder(5.35,5.35,.18,'#344e78',24);mat.position.y=1.58;trampoline.add(mat);
  const matCenter=cylinder(4.7,4.7,.04,'#426aa0',24);matCenter.position.y=1.7;trampoline.add(matCenter);
  const paddedRing=new THREE.Mesh(new THREE.TorusGeometry(5.6,.42,10,28),toon('#f1c84c'));paddedRing.rotation.x=Math.PI/2;paddedRing.position.y=1.62;trampoline.add(paddedRing);
  const innerRing=new THREE.Mesh(new THREE.TorusGeometry(5.08,.09,7,28),toon('#d9e7df'));innerRing.rotation.x=Math.PI/2;innerRing.position.y=1.71;trampoline.add(innerRing);
  for(let i=0;i<10;i+=1){
    const angle=i/10*Math.PI*2;
    const leg=roundedBox(.38,1.5,.38,i%2?'#4e5663':'#626a75',.08,2);leg.position.set(Math.cos(angle)*5.15,.75,Math.sin(angle)*5.15);leg.rotation.z=Math.cos(angle)*.14;trampoline.add(leg);
    const foot=roundedBox(1.0,.18,.72,'#3d424b',.07,2);foot.position.set(Math.cos(angle)*5.4,.09,Math.sin(angle)*5.4);foot.rotation.y=-angle;trampoline.add(foot);
  }
  for(let i=0;i<12;i+=1){
    if(i>=2&&i<=4)continue;
    const angle=i/12*Math.PI*2;
    const px=Math.cos(angle)*5.8;
    const pz=Math.sin(angle)*5.8;
    const pole=roundedBox(.2,4.55,.2,i%2?'#e7676e':'#4f8fba',.06,2);pole.position.set(px,3.82,pz);trampoline.add(pole);
    const cord=roundedBox(.055,3.55,.055,'#a8c6c4',.02,1);cord.position.set(Math.cos(angle)*5.72,3.88,Math.sin(angle)*5.72);trampoline.add(cord);
  }
  [2.0,5.7].forEach((y,index)=>{const hoop=new THREE.Mesh(new THREE.TorusGeometry(5.78,index ? .13 : .09,7,32),toon(index?'#e96d70':'#b9d1cd'));hoop.rotation.x=Math.PI/2;hoop.position.y=y;trampoline.add(hoop);});
  [-.9,.9].forEach((lx)=>{const rail=roundedBox(.18,2.2,.18,'#53616b',.05,2);rail.position.set(lx,1.08,6.2);rail.rotation.x=-.12;trampoline.add(rail);});
  [.35,.9,1.45].forEach((y)=>{const step=roundedBox(2.0,.16,.45,'#f0c44e',.06,2);step.position.set(0,y,6.25-y*.16);trampoline.add(step);});
  const sign=roundedBox(3.6,1.05,.16,'#e86970',.15,3);sign.position.set(0,6.25,-4.85);trampoline.add(sign);
  const signInset=roundedBox(2.7,.42,.08,'#f5d35e',.08,2);signInset.position.set(0,6.25,-4.96);trampoline.add(signInset);
  addAt(world.group,trampoline,x,0,z);
  world.trampolines.push({x,z,radius:5.15,height:1.72,bounceVelocity:9.8});
}

function addBedroomFixtures(world) {
  const dresserX=-29;
  const dresserZ=-11.5;
  const dresser=new THREE.Group();
  const dresserBody=roundedBox(8.4,3.4,2.8,'#d58a55',.2,4);dresserBody.position.y=1.7;dresser.add(dresserBody);
  [-1.05,1.05,2.72].forEach((y,index)=>{
    const drawer=roundedBox(7.45,.76,.08,index===1?'#e7a368':'#df965f',.09,3);drawer.position.set(0,y,1.44);dresser.add(drawer);
    [-2.15,2.15].forEach((x)=>{const handle=roundedBox(.85,.1,.1,'#654c40',.04,2);handle.position.set(x,y,1.53);dresser.add(handle);});
  });
  const tvFrame=roundedBox(6.6,3.8,.42,'#2b2b38',.2,4);tvFrame.position.set(0,5.45,.15);dresser.add(tvFrame);
  const tvScreen=roundedBox(6.0,3.2,.1,'#315b76',.15,3);tvScreen.position.set(0,5.45,.39);dresser.add(tvScreen);
  const tvStar=sphere(.38,'#f3d35a',12,8);tvStar.scale.z=.12;tvStar.position.set(1.8,6.2,.48);dresser.add(tvStar);
  addAt(world.group,dresser,dresserX,0,dresserZ,Math.PI);
  addLocalObstacle(world,dresserX,dresserZ,0,0,8.8,3.15,Math.PI,'bedroom-dresser',0,3.6);

  const nightstandX=-22;
  const nightstandZ=-24;
  const nightstand=roundedBox(3.2,2.3,3.2,'#efbd68',.2,4);nightstand.position.set(nightstandX,1.15,nightstandZ);world.group.add(nightstand);
  const nightDrawer=roundedBox(2.55,.7,.08,'#f5cf84',.1,3);nightDrawer.position.set(nightstandX,1.45,nightstandZ+1.64);world.group.add(nightDrawer);
  const nightKnob=sphere(.1,'#71533d',10,7);nightKnob.position.set(nightstandX,1.45,nightstandZ+1.73);world.group.add(nightKnob);
  const lampBase=cylinder(.62,.75,.22,'#5d83b8',12);lampBase.position.set(nightstandX,2.45,nightstandZ);world.group.add(lampBase);
  const lampStem=cylinder(.1,.12,1.5,'#6a5a4a',9);lampStem.position.set(nightstandX,3.25,nightstandZ);world.group.add(lampStem);
  const lampShade=new THREE.Mesh(new THREE.CylinderGeometry(.62,1.05,1.35,14),toon('#f06f6c'));lampShade.position.set(nightstandX,4.15,nightstandZ);world.group.add(lampShade);
  addObstacle(world,nightstandX,nightstandZ,3.55,3.55,'nightstand',0,2.35);

  const rackX=-10.5;
  const rackZ=-18.2;
  const rack=new THREE.Group();
  [-3.2,3.2].forEach((x)=>{const pole=roundedBox(.28,5.6,.28,'#6b584d',.07,2);pole.position.set(x,2.8,0);rack.add(pole);});
  const rail=roundedBox(6.7,.28,.28,'#6b584d',.07,2);rail.position.y=5.45;rack.add(rail);
  const lowerShelf=roundedBox(7.1,.32,2.2,'#b8784f',.12,3);lowerShelf.position.y=.42;rack.add(lowerShelf);
  [-2.15,-.72,.72,2.15].forEach((x,index)=>{
    const hanger=new THREE.Mesh(new THREE.TorusGeometry(.36,.055,6,12,Math.PI),toon('#d5c7b8'));hanger.rotation.z=Math.PI;hanger.position.set(x,4.95,0);rack.add(hanger);
    const shirt=roundedBox(1.18,2.5,.22,['#58a6c7','#e36f72','#f1c451','#746bc1'][index],.18,3);shirt.position.set(x,3.7,.08);rack.add(shirt);
  });
  addAt(world.group,rack,rackX,0,rackZ,.08);
  [-3.2,3.2].forEach((x)=>addLocalObstacle(world,rackX,rackZ,x,0,.55,.55,.08,'clothes-rack-pole',0,5.7));
  addLocalObstacle(world,rackX,rackZ,0,0,7.35,2.45,.08,'clothes-rack-shelf',.22,.7);

  const mirrorFrame=roundedBox(4.5,7.7,.3,'#e9b765',.22,4);mirrorFrame.position.set(-12.2,4.15,-world.depth/2+.34);world.group.add(mirrorFrame);
  const mirror=roundedBox(3.85,7.05,.09,'#8ec6d3',.18,3);mirror.position.set(-12.2,4.15,-world.depth/2+.55);world.group.add(mirror);
  const mirrorGlint=roundedBox(1.4,.08,.04,'#d9f4f2',.025,1);mirrorGlint.position.set(-12.75,6.45,-world.depth/2+.63);mirrorGlint.rotation.z=-.55;world.group.add(mirrorGlint);
}

function createArcadeCabinet(color,accent,labelColor) {
  const arcade=new THREE.Group();
  const body=roundedBox(3.8,6.4,3.25,color,.28,5);body.position.y=3.2;arcade.add(body);
  const upperCut=roundedBox(3.22,2.4,.32,'#242b3a',.16,3);upperCut.position.set(0,4.25,1.52);arcade.add(upperCut);
  const screen=roundedBox(2.72,1.82,.1,'#3157a0',.13,3);screen.position.set(0,4.3,1.72);arcade.add(screen);
  const screenOrb=sphere(.33,labelColor,12,8);screenOrb.scale.z=.12;screenOrb.position.set(.62,4.38,1.81);arcade.add(screenOrb);
  const control=roundedBox(3.55,.5,1.28,accent,.12,3);control.position.set(0,2.78,1.35);control.rotation.x=-.12;arcade.add(control);
  const stick=cylinder(.11,.11,.58,'#30313b',9);stick.position.set(-.85,3.14,1.4);arcade.add(stick);
  const stickTop=sphere(.2,'#ef5c62',10,7);stickTop.position.set(-.85,3.46,1.4);arcade.add(stickTop);
  [.45,.92].forEach((x,index)=>{const button=cylinder(.16,.16,.1,index?'#f5ce4e':'#62d0b8',10);button.position.set(x,3.12,1.52);arcade.add(button);});
  const marquee=roundedBox(3.35,1.0,.22,accent,.14,3);marquee.position.set(0,5.72,1.62);arcade.add(marquee);
  const marqueeInset=roundedBox(2.55,.42,.08,labelColor,.08,2);marqueeInset.position.set(0,5.72,1.76);arcade.add(marqueeInset);
  const coinDoor=roundedBox(1.75,1.4,.12,'#30313a',.12,3);coinDoor.position.set(0,1.25,1.68);arcade.add(coinDoor);
  const coin=sphere(.13,'#f1cb55',10,7);coin.scale.z=.3;coin.position.set(0,1.5,1.78);arcade.add(coin);
  return arcade;
}

function addToyRoomFixtures(world) {
  const shelfX=9.5;
  const shelfZ=-world.depth/2+1.65;
  const shelf=new THREE.Group();
  const shelfBack=roundedBox(12,7.4,2.75,'#efa84b',.24,5);shelfBack.position.y=3.7;shelf.add(shelfBack);
  [1.0,3.25,5.5].forEach((y)=>{const board=roundedBox(11.5,.28,3.05,'#ffd071',.09,3);board.position.y=y;shelf.add(board);});
  [-3.75,0,3.75].forEach((x)=>{const divider=roundedBox(.28,6.4,3,'#d8863f',.07,2);divider.position.set(x,4.2,0);shelf.add(divider);});
  [-3.75,0,3.75].forEach((x,index)=>{
    const bin=roundedBox(3.1,1.4,2.25,['#55a9c4','#e76872','#796bc0'][index],.18,4);bin.position.set(x,1.85,1.25);shelf.add(bin);
    const plush=sphere(.62,['#f4cc55','#5fc197','#ee8a62'][index],14,9);plush.position.set(x,4.25,.9);shelf.add(plush);
    [-.21,.21].forEach((eyeX)=>{const eye=sphere(.07,'#2b2b37',9,6);eye.position.set(x+eyeX,4.38,1.48);shelf.add(eye);});
  });
  addAt(world.group,shelf,shelfX,0,shelfZ);
  addObstacle(world,shelfX,shelfZ,12.5,3.25,'toy-shelf',0,7.6);

  const arcadeData=[
    [-8.5,'#5066bd','#f0b744','#ef675f'],
    [0,'#cf5867','#4f9fc2','#f3cf55'],
    [8.5,'#5aa171','#765fb3','#74d7e1'],
  ];
  arcadeData.forEach(([z,color,accent,label])=>{
    const arcade=createArcadeCabinet(color,accent,label);
    const x=world.width/2-2.15;
    addAt(world.group,arcade,x,0,z,-Math.PI/2);
    addLocalObstacle(world,x,z,0,0,4.15,3.65,-Math.PI/2,'arcade-machine',0,6.6);
  });

  const gameTableX=19;
  const gameTableZ=20.5;
  const gameTable=roundedBox(10,.58,5.4,'#6a62b8',.2,4);gameTable.position.set(gameTableX,3.25,gameTableZ);world.group.add(gameTable);
  [[-4,-1.9],[4,-1.9],[-4,1.9],[4,1.9]].forEach(([x,z])=>{
    const leg=roundedBox(.5,3.2,.5,'#40365f',.1,2);leg.position.set(gameTableX+x,1.6,gameTableZ+z);world.group.add(leg);
    addObstacle(world,gameTableX+x,gameTableZ+z,.82,.82,'game-table-leg',0,3.2);
  });
  addObstacle(world,gameTableX,gameTableZ,10.3,5.7,'game-table-top',2.96,3.58);
  const board=roundedBox(6.5,.08,3.7,'#f1d378',.08,2);board.position.set(gameTableX,3.6,gameTableZ);world.group.add(board);
  for(let i=0;i<10;i+=1){const pawn=cylinder(.14,.22,.42,i%2?'#e6535f':'#4187cf',9);pawn.position.set(gameTableX-2.4+(i%5)*1.2,3.85,gameTableZ+(i<5?-.75:.75));world.group.add(pawn);}
}

function createLiving() {
  const world = {
    id: 'living',
    group: new THREE.Group(),
    width: 40*MAP_SIZE_MULTIPLIER,
    depth: 30*MAP_SIZE_MULTIPLIER,
    obstacles: [],
    ramps: [],
    spawn: new THREE.Vector3(0, .02, 22),
    showcase: new THREE.Vector3(4, .02, 22),
    spawnYaw: 0,
    colors: { floor: '#d7a06b', map: '#9b6b4d', line: '#f1cf91' },
  };
  addRoomShell(world, world.width, world.depth, '#e9c9a2', ['#dba873', '#cc925e']);
  addSectorRug(world.group,-24,11,30,24,'#b55f52','#f1d083');
  addSectorRug(world.group,23,14,29,24,'#5d8068','#e8c965');

  // Alfombra de escala enorme.
  const rug = plane(19, 12, '#427f95');
  const rugX=-13;
  const rugZ=-20.5;
  rug.position.set(rugX, .015, rugZ);
  world.group.add(rug);
  for (let i = -6; i <= 6; i += 1) {
    const stripe = roundedBox(.24, .025, 11.7, i % 2 ? '#f4c95a' : '#f08a66', .01, 1);
    stripe.position.set(rugX + i * 1.25, .04, rugZ);
    stripe.rotation.y = i % 2 ? .03 : -.03;
    world.group.add(stripe);
  }

  // Sofá modular: cada bloque es varias veces más alto que el autito.
  const sofa = new THREE.Group();
  const sofaBase = roundedBox(10.5, 1.6, 3.7, '#b84f5b', .35, 4);
  sofaBase.position.y = .8;
  sofa.add(sofaBase);
  [-3.25, 0, 3.25].forEach((x) => {
    const cushion = roundedBox(3.0, .7, 2.65, '#d86468', .28, 4);
    cushion.position.set(x, 1.72, -.24);
    sofa.add(cushion);
    const back = roundedBox(3.02, 2.55, .78, '#c35660', .28, 4);
    back.position.set(x, 2.75, 1.32);
    back.rotation.x = -.09;
    sofa.add(back);
    [-.72, .72].forEach((buttonX) => {
      const button = sphere(.105, '#963d4d', 10, 7);
      button.scale.z = .42;
      button.position.set(x + buttonX, 2.85, .91);
      sofa.add(button);
    });
    const seam = roundedBox(2.62, .055, .06, '#f0817c', .02, 1);
    seam.position.set(x, 1.99, -1.54);
    sofa.add(seam);
  });
  [-5.05, 5.05].forEach((x) => {
    const arm = roundedBox(.7, 1.7, 3.5, '#a94756', .28, 4);
    arm.position.set(x, 1.35, 0);
    sofa.add(arm);
  });
  [-4.25, -1.45, 1.45, 4.25].forEach((x) => {
    const foot = roundedBox(.42, .38, .55, '#4b332d', .08, 2);
    foot.position.set(x, .05, .92);
    sofa.add(foot);
  });
  const sofaX=-13;
  const sofaZ=-12.8;
  addAt(world.group, sofa, sofaX, 0, sofaZ);
  addObstacle(world, sofaX, sofaZ, 11.3, 4.3, 'sofa', 0, 4.2);

  // Mesa ratona con espacio visible debajo, pero las patas sí colisionan.
  const table = new THREE.Group();
  const top = roundedBox(7, .48, 4.3, '#764a31', .22, 3);
  top.position.y = 2.3;
  top.material = standard('#ffffff', { roughness: .78 });
  top.material.map = plankTexture('#805239', '#68402f');
  top.material.map.repeat.set(2, 1);
  top.material.needsUpdate = true;
  table.add(top);
  const coffeeX=-13;
  const coffeeZ=-20.5;
  [[-2.8,-1.45],[2.8,-1.45],[-2.8,1.45],[2.8,1.45]].forEach(([x,z]) => {
    const leg = roundedBox(.48, 2.2, .48, '#5f3b29', .11, 2);
    leg.position.set(x, 1.1, z);
    table.add(leg);
    addObstacle(world, x + coffeeX, z + coffeeZ, .75, .75, 'coffee-table-leg', 0, 2.2);
  });
  const shelf = roundedBox(5.8, .22, 3.25, '#5a3929', .12, 2);
  shelf.position.y = .72;
  table.add(shelf);
  addAt(world.group, table, coffeeX, 0, coffeeZ);
  addObstacle(world,coffeeX,coffeeZ,7.2,4.5,'coffee-table-top',2.04,2.58);

  // Rack y TV.
  const tvX=-13;
  const tvZ=-world.depth/2+1.75;
  const rack = roundedBox(12, 1.55, 2.0, '#403348', .24, 3);
  rack.position.set(tvX, .78, tvZ);
  world.group.add(rack);
  for (let i = -2; i <= 2; i += 1) {
    const door = roundedBox(1.82, 1.05, .05, i % 2 ? '#51405d' : '#493852', .08, 2);
    door.position.set(tvX + i * 2.05, .78, tvZ+1.03);
    const knob = sphere(.08, '#f1cc65', 10, 7);
    knob.position.set(tvX + i * 2.05 + .55, .78, tvZ+1.1);
    world.group.add(door, knob);
  }
  addObstacle(world, tvX, tvZ+.15, 12.5, 2.5, 'tv-rack', 0, 1.7);
  const tvFrame = roundedBox(9.5, 5.5, .48, '#282633', .25, 3);
  tvFrame.position.set(tvX, 4.5, tvZ);
  const tvScreen = roundedBox(8.8, 4.8, .12, '#263e5c', .18, 3);
  tvScreen.position.set(0, 0, .3);
  tvFrame.add(tvScreen);
  const tvReflection = roundedBox(2.8, .07, .035, '#7cb8e6', .02, 1);
  tvReflection.position.set(-1.6, 1.55, .38);
  tvReflection.rotation.z = -.32;
  tvFrame.add(tvReflection);
  const tvStand = roundedBox(2.4, .18, .75, '#22212c', .07, 2);
  tvStand.position.set(tvX, 1.65, tvZ+.43);
  world.group.add(tvStand);
  world.group.add(tvFrame);
  const tvGlow = new THREE.PointLight(0x5b8cff, 8, 12, 2);
  tvGlow.position.set(tvX, 4.5, tvZ+2.75);
  world.group.add(tvGlow);

  // Comedor con mesa altísima y seis sillas.
  const dining = new THREE.Group();
  const diningX=-25.2;
  const diningZ=8.5;
  const diningTop = roundedBox(9, .55, 5.3, '#e5bd77', .2, 3);
  diningTop.position.y = 4.2;
  dining.add(diningTop);
  [[-3.7,-1.85],[3.7,-1.85],[-3.7,1.85],[3.7,1.85]].forEach(([x,z]) => {
    const leg = roundedBox(.52, 4.05, .52, '#845d3f', .08, 2);
    leg.position.set(x, 2, z);
    dining.add(leg);
  });
  addAt(world.group, dining, diningX, 0, diningZ, -.05);
  addLocalObstacle(world,diningX,diningZ,0,0,9.2,5.5,-.05,'dining-table-top',3.9,4.52);
  [[-3.7,-1.85],[3.7,-1.85],[-3.7,1.85],[3.7,1.85]].forEach(([x,z])=>{
    addLocalObstacle(world,diningX,diningZ,x,z,.9,.9,-.05,'dining-table-leg',0,4.1);
  });
  [[-.2,-3.7,Math.PI],[-.2,3.7,0],[-5.4,0,-Math.PI/2],[5.4,0,Math.PI/2]].forEach(([offsetX,offsetZ,r]) => {
    const x=diningX+offsetX;
    const z=diningZ+offsetZ;
    const chair = new THREE.Group();
    const seat = roundedBox(2.2,.42,2.1,'#efb54c',.14,3); seat.position.y=2.05;
    const chairTop = roundedBox(2.2,.38,.38,'#db853d',.13,3); chairTop.position.set(0,4.82,.85);
    chair.add(seat,chairTop);
    [-.72,0,.72].forEach((sx)=>{const slat=roundedBox(.25,2.55,.3,'#db853d',.08,2);slat.position.set(sx,3.48,.85);chair.add(slat);});
    [-.78,.78].forEach((lx)=>[-.72,.72].forEach((lz)=>{const leg=roundedBox(.25,2,.25,'#70462f',.06,2);leg.position.set(lx,1,lz);chair.add(leg);}));
    addAt(world.group,chair,x,0,z,r);
    addLocalObstacle(world,x,z,0,0,2.3,2.2,r,'chair-seat',1.82,2.3);
    addLocalObstacle(world,x,z,0,.85,2.3,.48,r,'chair-back',2.15,5.05);
    [-.78,.78].forEach((lx)=>[-.72,.72].forEach((lz)=>{
      addLocalObstacle(world,x,z,lx,lz,.48,.48,r,'chair-leg',0,2.05);
    }));
  });

  addDiningAreaDetails(world);

  addKitchenArea(world);
  addReadingArea(world);

  const plantX=-world.width/2+3.1;
  const plantZ=-world.depth/2+3.2;
  addPlant(world.group, plantX, plantZ, 1.25);
  addCircleObstacle(world, plantX, plantZ, 1, 'plant-pot', 0, 1.2);
  addWallArt(world.group, -4.8, 7.5, -world.depth/2+.28, 3.2, 2.35, '#61a574');
  addWallArt(world.group, -world.width/2+.28, 6.1, -5*MAP_SIZE_MULTIPLIER, 3.4, 4.4, '#d86862', true);
  addLivingWallDetails(world);

  // Revistas y objetos pequeños que venden la escala.
  const magazine = roundedBox(2.4, .09, 1.7, '#f6d654', .05, 2);
  magazine.position.set(-5.5, .08, -6.5); magazine.rotation.y = .3; world.group.add(magazine);
  const magazinePage = roundedBox(2.18, .035, 1.5, '#f8f1d8', .035, 2);
  magazinePage.position.set(-5.5, .135, -6.5); magazinePage.rotation.y = .3; world.group.add(magazinePage);
  const vase = new THREE.Group();
  const vaseBody = sphere(.48, '#67a8cf', 18, 12); vaseBody.scale.set(1,1.18,1); vaseBody.position.y=.55;
  const vaseNeck = cylinder(.22,.32,.55,'#79b9d8',16); vaseNeck.position.y=1.05;
  vase.add(vaseBody,vaseNeck);
  [-.22,0,.22].forEach((x,index)=>{
    const stem=cylinder(.025,.035,.82,'#3e8b52',7);stem.position.set(x*.45,1.63,index===1?.05:0);stem.rotation.z=x*.35;vase.add(stem);
    const bloom=sphere(.2,['#f06d73','#f3cb4f','#e985ba'][index],12,8);bloom.position.set(x,2.03,index===1?.05:0);vase.add(bloom);
  });
  addAt(world.group,vase,coffeeX,2.55,coffeeZ);

  return world;
}

function createBedroom() {
  const world = {
    id: 'bedroom',
    group: new THREE.Group(),
    width: 38*MAP_SIZE_MULTIPLIER,
    depth: 30*MAP_SIZE_MULTIPLIER,
    obstacles: [],
    ramps: [],
    trampolines: [],
    spawn: new THREE.Vector3(0, .02, 10*MAP_SIZE_MULTIPLIER),
    showcase: new THREE.Vector3(-3, .02, 23),
    spawnYaw: 0,
    colors: { floor: '#6f9ec0', map: '#526f9d', line: '#d8f0f2' },
  };
  addRoomShell(world, world.width, world.depth, '#9bc6d4', ['#dfb568', '#d2a456']);
  addBedroomWallDetails(world);
  addSectorRug(world.group,-20,-17,33,24,'#8db5c8','#f1d06a');
  addSectorRug(world.group,20,8,31,28,'#596cb5','#ee8a68');
  addKidsTrampoline(world,-22,12);

  // Cama completa con manta cayendo.
  const bedX=-world.width/2+7.2;
  const bedZ=-world.depth/2+6.5;
  const bed = new THREE.Group();
  const frame = roundedBox(12.2, 1.45, 8.7, '#557dc0', .28, 4); frame.position.y = .75;
  const mattress = roundedBox(11.7, 1.25, 8.2, '#fff5d5', .34, 4); mattress.position.y = 1.85;
  const blanket = roundedBox(11.8, .25, 5, '#e86267', .11, 3); blanket.position.set(0,2.54,1.45);
  const pillowA = roundedBox(4.1,.65,2.2,'#f5d65a',.28,4); pillowA.position.set(-2.7,2.78,-2.25); pillowA.rotation.y=.1;
  const pillowB = pillowA.clone(); pillowB.position.x=2.1; pillowB.rotation.y=-.1;
  const headboard = roundedBox(12.4, 3.9, .65, '#426aaa', .3, 4); headboard.position.set(0,2.15,-4.05);
  const headboardInset = roundedBox(10.8, 2.45, .14, '#6f96d2', .24, 3); headboardInset.position.set(0,2.35,-3.68);
  bed.add(frame,mattress,blanket,pillowA,pillowB,headboard,headboardInset);
  [-4.25, 0, 4.25].forEach((x) => {
    const blanketBand = roundedBox(2.15,.035,4.75,x === 0 ? '#f4c852' : '#f28d73',.02,1);
    blanketBand.position.set(x,2.69,1.45);bed.add(blanketBand);
  });
  [-5.35,5.35].forEach((x)=>{
    const bedFoot=roundedBox(.6,.7,.65,'#334f83',.12,2);bedFoot.position.set(x,.12,3.55);bed.add(bedFoot);
  });
  const pillowSeamA=roundedBox(3.65,.035,.05,'#e1b93e',.015,1);pillowSeamA.position.set(-2.7,3.115,-3.05);pillowSeamA.rotation.y=.1;bed.add(pillowSeamA);
  const pillowSeamB=pillowSeamA.clone();pillowSeamB.position.x=2.1;pillowSeamB.rotation.y=-.1;bed.add(pillowSeamB);
  addAt(world.group, bed, bedX, 0, bedZ);
  addObstacle(world,bedX,bedZ,12.8,9.2,'bed',0,3.2);

  // Escritorio, cajones y monitor.
  const deskX=26;
  const deskZ=-world.depth/2+2.4;
  const desk = new THREE.Group();
  const deskTop = roundedBox(10,.6,3.9,'#e6a84f',.2,3); deskTop.position.y=4;
  deskTop.material=standard('#ffffff',{roughness:.8});deskTop.material.map=plankTexture('#e6a84f','#c9813e');deskTop.material.map.repeat.set(2,1);deskTop.material.needsUpdate=true;
  const drawers = roundedBox(2.8,3.8,3.5,'#d78343',.16,3); drawers.position.set(3.2,1.9,0);
  const leg = roundedBox(.6,3.8,.6,'#7b5238',.1,2); leg.position.set(-4,1.9,0);
  const monitor = roundedBox(5.2,3.1,.35,'#343141',.17,3); monitor.position.set(-.5,5.9,-.35);
  const screen = roundedBox(4.65,2.55,.1,'#584be0',.12,2); screen.position.z=.22; monitor.add(screen);
  const screenMoon=sphere(.45,'#f4d65d',12,8);screenMoon.scale.z=.12;screenMoon.position.set(1.25,.62,.3);monitor.add(screenMoon);
  [-.9,-.3,.3].forEach((x,i)=>{const hill=sphere(.72+i*.15,['#45b98a','#36a477','#278d69'][i],12,8);hill.scale.set(1,.5,.1);hill.position.set(x,-.75,.3);monitor.add(hill);});
  [1.05,2.05,3.05].forEach((y,i)=>{
    const face=roundedBox(2.35,.74,.08,i%2?'#c56f3b':'#e39747',.09,2);face.position.set(3.2,y,1.79);desk.add(face);
    const handle=roundedBox(.75,.12,.12,'#674337',.05,2);handle.position.set(3.2,y,1.86);desk.add(handle);
  });
  desk.add(deskTop,drawers,leg,monitor);
  addAt(world.group,desk,deskX,0,deskZ);
  addObstacle(world,deskX+3.2,deskZ,3.3,4.3,'drawers',0,3.9);
  addObstacle(world,deskX-4,deskZ,1.1,1.1,'desk-leg',0,3.9);

  const wardrobeX=-4.8;
  const wardrobeZ=-world.depth/2+2.15;
  const wardrobe = roundedBox(6.2,9.8,3.2,'#6e5aa7',.25,4); wardrobe.position.set(wardrobeX,4.9,wardrobeZ); world.group.add(wardrobe);
  const seam = roundedBox(.09,8.9,3.23,'#8f75c3',.025,1); seam.position.set(wardrobeX,5,wardrobeZ); world.group.add(seam);
  [-1,1].forEach((s)=>{
    const panel=roundedBox(2.5,8.55,.1,s>0?'#7761b2':'#806abc',.16,3);panel.position.set(wardrobeX+s*1.43,5,wardrobeZ+1.63);world.group.add(panel);
    const inner=roundedBox(1.85,7.65,.08,'#6e58a6',.13,2);inner.position.set(wardrobeX+s*1.43,5,wardrobeZ+1.7);world.group.add(inner);
    const handle=sphere(.14,'#f0d26b',10,6);handle.position.set(wardrobeX+s*.33,5,wardrobeZ+1.8);world.group.add(handle);
  });
  addObstacle(world,wardrobeX,wardrobeZ,6.7,3.7,'wardrobe',0,9.9);

  addBedroomFixtures(world);
  addToyRoomFixtures(world);

  // Bloques desparramados.
  const random = seededRandom(731);
  const blockColors = ['#ef5f58','#f3c948','#3bb486','#4b7fe3','#a85ed6'];
  const reservedToySpots=[
    {x:7,z:-8,radius:4},
    {x:20,z:8,radius:4.5},
    {x:9,z:15,radius:4},
    {x:29,z:18,radius:4.5},
    {x:world.spawn.x,z:world.spawn.z,radius:7},
  ];
  for (let i=0;i<26;i+=1) {
    let x=4;
    let z=0;
    let foundSpot=false;
    for(let attempt=0;attempt<14;attempt+=1){
      x=4+random()*27;
      z=-8+random()*28;
      const reserved=reservedToySpots.some((spot)=>Math.hypot(x-spot.x,z-spot.z)<spot.radius);
      if(!reserved&&!pointNearObstacle(world,x,z,1.25)){foundSpot=true;break;}
    }
    if(!foundSpot)continue;
    const size = .7 + random()*.85;
    const blockGroup=new THREE.Group();
    const block = roundedBox(size,size,size,blockColors[i%blockColors.length],.12,2);
    block.position.y=size/2;blockGroup.add(block);
    const studCount=size>1.15?2:1;
    for(let sx=0;sx<studCount;sx+=1){for(let sz=0;sz<studCount;sz+=1){
      const stud=cylinder(size*.14,size*.14,size*.12,blockColors[i%blockColors.length],10);
      stud.position.set((sx-(studCount-1)/2)*size*.42,size*1.04,(sz-(studCount-1)/2)*size*.42);blockGroup.add(stud);
    }}
    blockGroup.position.set(x,0,z); blockGroup.rotation.set(random()*.25,random()*Math.PI,random()*.2);
    world.group.add(blockGroup);
    addObstacle(world,x,z,size*.9,size*.9,'toy-block',0,size*1.18);
  }

  // Pelota gigante, tren y robot.
  const ballX=7;
  const ballZ=-8;
  const ball = sphere(2.2,'#f0db4d',20,12); ball.position.set(ballX,2.1,ballZ); world.group.add(ball);
  const ballStripe = new THREE.Mesh(new THREE.TorusGeometry(2.21,.16,8,24),toon('#e85553')); ballStripe.position.copy(ball.position); ballStripe.rotation.x=Math.PI/2; world.group.add(ballStripe);
  addCircleObstacle(world,ballX,ballZ,2.15,'ball',0,4.3);

  const train = new THREE.Group();
  const trainBody=roundedBox(4.4,1.3,1.9,'#e34f51',.22,3);trainBody.position.y=.9;
  const cabin=roundedBox(1.5,1.6,1.7,'#4179c6',.18,3);cabin.position.set(1.1,1.65,0);
  const chimney=cylinder(.35,.47,1.5,'#34313c',10);chimney.position.set(-1.15,1.9,0);
  const boiler=cylinder(.7,.7,2.35,'#ee6e59',16);boiler.rotation.z=Math.PI/2;boiler.position.set(-.65,1.55,0);
  const boilerBands=[-.35,-1.15].map((x)=>{const band=new THREE.Mesh(new THREE.TorusGeometry(.71,.07,6,14),toon('#f3c64d'));band.rotation.y=Math.PI/2;band.position.set(x,1.55,0);return band;});
  const trainLamp=sphere(.22,'#fff0a1',12,8);trainLamp.position.set(-2.2,1.65,0);
  const cowcatcher=roundedBox(.22,.85,2.35,'#f0b840',.08,2);cowcatcher.position.set(-2.28,.62,0);cowcatcher.rotation.z=-.28;
  train.add(trainBody,cabin,chimney,boiler,...boilerBands,trainLamp,cowcatcher);
  [-1.4,1.4].forEach(x=>[-1,1].forEach(z=>{const wheel=cylinder(.48,.48,.24,'#2c2934',12);wheel.rotation.x=Math.PI/2;wheel.position.set(x,.48,z*.94);train.add(wheel);}));
  const trainX=20;
  const trainZ=8;
  addAt(world.group,train,trainX,0,trainZ,.35); addObstacle(world,trainX,trainZ,5,3.5,'toy-train',0,2.8);

  const robot = new THREE.Group();
  const robotBody=roundedBox(2.3,2.6,1.5,'#64b4bc',.23,3);robotBody.position.y=2.1;
  const robotHead=roundedBox(1.9,1.5,1.45,'#efb94f',.25,3);robotHead.position.y=4.25;
  [-.45,.45].forEach(x=>{const eye=sphere(.13,'#272533',10,7);eye.position.set(x,4.4,-.73);robot.add(eye);});
  const mouth=roundedBox(.65,.1,.06,'#8f602f',.03,1);mouth.position.set(0,3.96,-.75);
  const antenna=cylinder(.055,.075,.75,'#383441',7);antenna.position.y=5.3;
  const antennaTip=sphere(.16,'#ef5d65',10,7);antennaTip.position.y=5.72;
  [-1,1].forEach((side)=>{
    const arm=roundedBox(.42,2.1,.45,'#4b929d',.15,3);arm.position.set(side*1.42,2.25,0);arm.rotation.z=-side*.16;robot.add(arm);
    const hand=sphere(.32,'#efb94f',10,7);hand.position.set(side*1.6,1.2,0);robot.add(hand);
    const leg=roundedBox(.62,1.05,.7,'#4b738f',.16,3);leg.position.set(side*.62,.55,0);robot.add(leg);
  });
  const chestDial=cylinder(.34,.34,.08,'#f06c64',12);chestDial.rotation.x=Math.PI/2;chestDial.position.set(0,2.25,-.78);
  const robotX=9;
  const robotZ=15;
  robot.add(robotBody,robotHead,mouth,antenna,antennaTip,chestDial); addAt(world.group,robot,robotX,0,robotZ,-.2); addObstacle(world,robotX,robotZ,3.5,2.4,'robot',0,5.9);

  // Libros apilados como obstáculo bajo.
  const booksX=29;
  const booksZ=18;
  const bookA=roundedBox(5.5,.48,3.4,'#4e83d4',.12,3);bookA.position.set(booksX,.25,booksZ+.15);bookA.rotation.y=-.08;world.group.add(bookA);
  const bookB=roundedBox(5.5,.48,3.4,'#ee655d',.12,3);bookB.position.set(booksX,.75,booksZ-.15);bookB.rotation.y=.06;world.group.add(bookB);
  const pagesA=roundedBox(5.05,.3,3.12,'#f4e8c4',.08,2);pagesA.position.set(booksX,.28,booksZ+.15);pagesA.rotation.y=-.08;world.group.add(pagesA);
  const pagesB=roundedBox(5.05,.3,3.12,'#fff0cf',.08,2);pagesB.position.set(booksX,.77,booksZ-.15);pagesB.rotation.y=.06;world.group.add(pagesB);
  addObstacle(world,booksX,booksZ,5.8,3.8,'book-stack',0,1.15);

  return world;
}

function createFlower(group, x, z, color, scale = 1, y = 0) {
  const flower = new THREE.Group();
  const stem = cylinder(.055*scale,.075*scale,1.25*scale,'#3d8743',7);stem.position.y=.62*scale;flower.add(stem);
  for(let i=0;i<6;i+=1){const petal=sphere(.22*scale,color,9,6);const a=i/6*Math.PI*2;petal.scale.set(1,.5,.65);petal.position.set(Math.cos(a)*.28*scale,1.35*scale,Math.sin(a)*.28*scale);flower.add(petal);}
  const center=sphere(.18*scale,'#ffd34d',10,7);center.position.y=1.37*scale;flower.add(center);
  addAt(group,flower,x,y,z);
}

function addGardenHouseFacade(world) {
  const facadeZ=world.depth/2-.18;
  const wall=box(world.width-.4,11.2,.72,'#efd2a9');wall.position.set(0,5.6,facadeZ);world.group.add(wall);
  const foundation=roundedBox(world.width,.72,.92,'#b99a7e',.09,2);foundation.position.set(0,.36,facadeZ-.08);world.group.add(foundation);
  const eave=roundedBox(world.width+1.1,1.05,2.15,'#70443d',.16,3);eave.position.set(0,11.18,facadeZ-.12);world.group.add(eave);
  const gutter=roundedBox(world.width+.5,.3,.38,'#d8c7ae',.08,2);gutter.position.set(0,10.72,facadeZ-1.12);world.group.add(gutter);
  for(let y=1.35;y<10.3;y+=1.05){const siding=roundedBox(world.width-.8,.07,.06,'#d9b98f',.025,1);siding.position.set(0,y,facadeZ-.41);world.group.add(siding);}

  const garageX=-35;
  const garage=roundedBox(20.2,7.25,.28,'#d9e2df',.22,4);garage.position.set(garageX,3.8,facadeZ-.5);world.group.add(garage);
  [-6.2,0,6.2].forEach((x)=>{const seam=roundedBox(.12,6.55,.08,'#a8b6b4',.04,2);seam.position.set(garageX+x,3.8,facadeZ-.67);world.group.add(seam);});
  [1.25,3.05,4.85,6.65].forEach((y)=>{const seam=roundedBox(19.3,.1,.08,'#a8b6b4',.04,2);seam.position.set(garageX,y,facadeZ-.67);world.group.add(seam);});
  const garageFrame=roundedBox(21,.42,.55,'#8f6a55',.08,2);garageFrame.position.set(garageX,7.45,facadeZ-.57);world.group.add(garageFrame);
  [-10.3,10.3].forEach((x)=>{const side=roundedBox(.42,7.65,.55,'#8f6a55',.08,2);side.position.set(garageX+x,3.8,facadeZ-.57);world.group.add(side);});
  const garageHandle=roundedBox(3.4,.22,.12,'#6f7778',.06,2);garageHandle.position.set(garageX,1.05,facadeZ-.77);world.group.add(garageHandle);

  const doorX=-19.7;
  const door=roundedBox(5.4,8.1,.3,'#4986a0',.2,4);door.position.set(doorX,4.1,facadeZ-.52);world.group.add(door);
  const doorInset=roundedBox(4.35,3.0,.12,'#6ca9b5',.14,3);doorInset.position.set(doorX,5.65,facadeZ-.72);world.group.add(doorInset);
  const doorLower=roundedBox(4.35,2.55,.12,'#3d718e',.14,3);doorLower.position.set(doorX,2.25,facadeZ-.72);world.group.add(doorLower);
  const doorKnob=sphere(.18,'#f0c44e',10,7);doorKnob.position.set(doorX+1.75,4.05,facadeZ-.85);world.group.add(doorKnob);
  const step=roundedBox(6.8,.45,2.35,'#b5aa96',.14,3);step.position.set(doorX,.22,facadeZ-1.3);world.group.add(step);addObstacle(world,doorX,facadeZ-1.3,7.1,2.7,'house-step',0,.5);

  const addWindow=(x,y=6,width=7,height=4.5)=>{
    const frame=roundedBox(width+.55,height+.55,.32,'#fff3dc',.13,3);frame.position.set(x,y,facadeZ-.5);world.group.add(frame);
    const glass=roundedBox(width,height,.12,'#77b8ca',.1,3);glass.position.set(x,y,facadeZ-.72);world.group.add(glass);
    const vertical=roundedBox(.18,height-.18,.1,'#f4ead7',.05,2);vertical.position.set(x,y,facadeZ-.82);world.group.add(vertical);
    const horizontal=roundedBox(width-.18,.18,.1,'#f4ead7',.05,2);horizontal.position.set(x,y,facadeZ-.82);world.group.add(horizontal);
    const sill=roundedBox(width+1,.28,.72,'#c8a27e',.08,2);sill.position.set(x,y-height/2-.35,facadeZ-.66);world.group.add(sill);
  };
  addWindow(-9.5,6.2,7.2,4.6);
  addWindow(35.5,6.2,8,4.8);

  const patioDoorX=12.5;
  const patioFrame=roundedBox(17.4,8.8,.35,'#fff1dc',.18,4);patioFrame.position.set(patioDoorX,4.55,facadeZ-.5);world.group.add(patioFrame);
  [-5.55,0,5.55].forEach((x)=>{const panel=roundedBox(5.05,7.75,.12,x===0?'#79b7c3':'#70adbc',.1,3);panel.position.set(patioDoorX+x,4.55,facadeZ-.72);world.group.add(panel);});
  [-2.82,2.82].forEach((x)=>{const divider=roundedBox(.2,8.05,.12,'#f3e7d4',.05,2);divider.position.set(patioDoorX+x,4.55,facadeZ-.83);world.group.add(divider);});
  const patioHandle=roundedBox(.18,2.0,.13,'#5a6a69',.05,2);patioHandle.position.set(patioDoorX+.72,4.3,facadeZ-.88);world.group.add(patioHandle);
  const patioStep=roundedBox(19.5,.38,2.55,'#b5aa96',.13,3);patioStep.position.set(patioDoorX,.19,facadeZ-1.4);world.group.add(patioStep);addObstacle(world,patioDoorX,facadeZ-1.4,19.8,2.85,'patio-step',0,.43);

  [-14.8,25.2,43].forEach((x,index)=>{
    const mount=roundedBox(.5,.72,.28,'#513c35',.1,3);mount.position.set(x,7.55,facadeZ-.7);world.group.add(mount);
    const lamp=cylinder(.32,.55,1.1,index%2?'#f4ce68':'#f2b754',10);lamp.position.set(x,6.9,facadeZ-.88);world.group.add(lamp);
  });
  [-8.8,35.2].forEach((x)=>{
    const planter=roundedBox(7.5,.75,1.15,'#c96f4f',.18,3);planter.position.set(x,2.95,facadeZ-1);world.group.add(planter);
    for(let i=-2;i<=2;i+=1){createFlower(world.group,x+i*1.25,facadeZ-1.35,i%2?'#f3c84e':'#ee6c7b',.55,3.22);}
  });

  addObstacle(world,0,facadeZ,world.width,.95,'house-facade',0,11.8);
}

function createGarden() {
  const world = {
    id: 'garden',
    group: new THREE.Group(),
    width: 48*MAP_SIZE_MULTIPLIER,
    depth: 36*MAP_SIZE_MULTIPLIER,
    obstacles: [],
    ramps: [],
    spawn: new THREE.Vector3(-9*MAP_SIZE_MULTIPLIER, .02, 10.5*MAP_SIZE_MULTIPLIER),
    showcase: new THREE.Vector3(0, .02, 10*MAP_SIZE_MULTIPLIER),
    // Mira hacia el centro y deja la cámara libre del tablón-rampa cercano.
    spawnYaw: 0,
    colors: { floor: '#579750', map: '#3f7943', line: '#d4b978' },
  };
  const grass = new THREE.Mesh(new THREE.BoxGeometry(world.width,.5,world.depth),standard('#62a554',{roughness:1}));
  grass.material.map=speckleTexture('#62a554','#397a42',1903);grass.material.map.repeat.set(30,24);grass.material.needsUpdate=true;
  grass.position.y=-.27;grass.receiveShadow=true;world.group.add(grass);

  // Manchones de césped para darle volumen sin usar texturas externas.
  const random=seededRandom(1903);
  for(let i=0;i<170*MAP_SIZE_MULTIPLIER;i+=1){
    const blade=cylinder(.025,.06,.35+random()*.35,i%3===0?'#78bd5f':'#4e9348',5);
    blade.position.set(-world.width/2+1+random()*(world.width-2),.2,-world.depth/2+1+random()*(world.depth-2));
    blade.rotation.z=(random()-.5)*.4;
    world.group.add(blade);
  }

  // Sendero de tierra serpenteante, armado con discos solapados.
  const pathStoneCount=14*MAP_SIZE_MULTIPLIER+1;
  for(let i=0;i<pathStoneCount;i+=1){
    const path=sphere(3.2,'#bb8a56',16,7);path.scale.set(1,.035,.64);path.position.set(-18*MAP_SIZE_MULTIPLIER+i*2.65,.03,Math.sin(i*.42)*4*MAP_SIZE_MULTIPLIER);world.group.add(path);
  }

  // Cerca alta que enmarca la arena.
  const fenceColor='#d6a45f';
  const fenceX=world.width/2-.4;
  const fenceZ=world.depth/2-.4;
  for(let x=-world.width/2+1;x<=world.width/2-1;x+=4){
    const post=roundedBox(1.35,4.2,.4,fenceColor,.12,2);post.position.set(x,2.1,-fenceZ);world.group.add(post);
    const cap=roundedBox(1.48,.22,.52,'#edc078',.08,2);cap.position.set(x,4.18,-fenceZ);world.group.add(cap);
  }
  for(let z=-world.depth/2+2;z<=world.depth/2-2;z+=4){
    const postL=roundedBox(.4,4.2,1.35,fenceColor,.12,2);postL.position.set(-fenceX,2.1,z);world.group.add(postL);
    const postR=postL.clone();postR.position.x=fenceX;world.group.add(postR);
    const capL=roundedBox(.52,.22,1.48,'#edc078',.08,2);capL.position.set(-fenceX,4.18,z);world.group.add(capL);
    const capR=capL.clone();capR.position.x=fenceX;world.group.add(capR);
  }
  const rails=[1.1,3].flatMap(y=>{
    const back=roundedBox(world.width-1,.32,.38,'#9b6b41',.07,2);back.position.set(0,y,-world.depth/2+.65);
    const left=roundedBox(.38,.32,world.depth-2,'#9b6b41',.07,2);left.position.set(-world.width/2+.65,y,0);
    const right=left.clone();right.position.x=world.width/2-.65;
    return [back,left,right];
  });world.group.add(...rails);

  addGardenHouseFacade(world);

  // Cantero, plantas y piedras.
  const flowerBedX=13*MAP_SIZE_MULTIPLIER;
  const flowerBedZ=-10*MAP_SIZE_MULTIPLIER;
  const bed=roundedBox(14,.55,5.5,'#8e5a39',.23,3);bed.position.set(flowerBedX,.25,flowerBedZ);world.group.add(bed);addObstacle(world,flowerBedX,flowerBedZ,14.6,6,'flower-bed',0,.75);
  const bedSoil=roundedBox(13.3,.18,4.8,'#513b2c',.18,3);bedSoil.position.set(flowerBedX,.62,flowerBedZ);world.group.add(bedSoil);
  for(let i=0;i<14;i+=1){createFlower(world.group,flowerBedX-6+random()*12,flowerBedZ-2+random()*4,['#f06d73','#f4d053','#8f6bd8','#f08ac0'][i%4],.75+random()*.5);}
  [[-17*MAP_SIZE_MULTIPLIER,-11*MAP_SIZE_MULTIPLIER,1.4],[-14*MAP_SIZE_MULTIPLIER,-13*MAP_SIZE_MULTIPLIER,1],[-19*MAP_SIZE_MULTIPLIER,-7*MAP_SIZE_MULTIPLIER,1.1],[18*MAP_SIZE_MULTIPLIER,9*MAP_SIZE_MULTIPLIER,1.5],[15*MAP_SIZE_MULTIPLIER,12*MAP_SIZE_MULTIPLIER,1]].forEach(([x,z,s],i)=>{
    const rock=irregularRock(s,i%2?'#8d8d82':'#a7a294',i+4);rock.scale.set(1.25,.7,.9);rock.position.set(x,s*.55,z);rock.rotation.y=random()*Math.PI;world.group.add(rock);addCircleObstacle(world,x,z,s*1.05,'rock',0,s*1.25);
  });

  // Fuente y gnomo de jardín.
  const fountain=new THREE.Group();
  const fountainX=1*MAP_SIZE_MULTIPLIER;
  const fountainZ=-8*MAP_SIZE_MULTIPLIER;
  const fountainBase=cylinder(3.4,3.7,.65,'#b7b4a7',18);fountainBase.position.y=.32;
  const bowl=cylinder(2.6,3.1,.55,'#d3d0c2',18);bowl.position.y=.83;
  const water=cylinder(2.45,2.45,.08,'#5fc5dc',20);water.position.y=1.13;
  const center=cylinder(.48,.72,2.8,'#bbb8aa',14);center.position.y=2.2;
  const topBowl=cylinder(1.25,1.6,.42,'#d2cfc2',16);topBowl.position.y=3.3;
  const topWater=cylinder(1.18,1.18,.07,'#79daea',18);topWater.position.y=3.53;
  fountain.add(fountainBase,bowl,water,center,topBowl,topWater);
  [[-1,0],[1,0],[0,-1],[0,1]].forEach(([dx,dz])=>{
    const jet=tubeBetween(new THREE.Vector3(dx*.72,3.45,dz*.72),new THREE.Vector3(dx*1.72,1.25,dz*1.72),.055,'#8de8f2',7);fountain.add(jet);
    const drop=sphere(.11,'#baf5f7',9,6);drop.position.set(dx*1.72,1.28,dz*1.72);fountain.add(drop);
  });
  addAt(world.group,fountain,fountainX,0,fountainZ);addCircleObstacle(world,fountainX,fountainZ,3.55,'fountain',0,3.75);
  const waterLight=new THREE.PointLight(0x65dfff,5,9,2);waterLight.position.set(fountainX,2,fountainZ);world.group.add(waterLight);

  const gnome=new THREE.Group();
  const gnomeX=-9*MAP_SIZE_MULTIPLIER;
  const gnomeZ=-10*MAP_SIZE_MULTIPLIER;
  const gnomeBody=sphere(1.05,'#3f75bd',14,9);gnomeBody.scale.y=1.25;gnomeBody.position.y=1.15;
  const face=sphere(.72,'#f0ba91',14,9);face.position.set(0,2.15,-.17);
  const hat=new THREE.Mesh(new THREE.ConeGeometry(.82,2.1,12),toon('#dd4e4e'));hat.position.set(0,3.35,0);
  const beard=sphere(.62,'#f7f2dc',14,8);beard.scale.set(.8,1.2,.45);beard.position.set(0,1.75,-.65);
  const nose=sphere(.19,'#db936e',10,7);nose.position.set(0,2.14,-.83);
  [-.25,.25].forEach((x)=>{const eye=sphere(.07,'#272532',9,6);eye.position.set(x,2.37,-.82);gnome.add(eye);});
  [-.42,.42].forEach((x)=>{const shoe=roundedBox(.72,.35,1.0,'#44352e',.15,3);shoe.position.set(x,.22,-.18);gnome.add(shoe);});
  [1.0,1.38].forEach((y)=>{const button=sphere(.08,'#f2cb4e',9,6);button.position.set(0,y,-.93);gnome.add(button);});
  gnome.add(gnomeBody,face,hat,beard,nose);addAt(world.group,gnome,gnomeX,0,gnomeZ,.2);addCircleObstacle(world,gnomeX,gnomeZ,1.18,'gnome',0,4.45);

  // Mesa de picnic.
  const picnicX=12*MAP_SIZE_MULTIPLIER;
  const picnicZ=7*MAP_SIZE_MULTIPLIER;
  const picnic=new THREE.Group();
  const top=roundedBox(9,.55,3,'#c88545',.2,3);top.position.y=3.5;picnic.add(top);
  [-.93,0,.93].forEach((z,i)=>{const plank=roundedBox(8.75,.13,.82,i%2?'#dd9c55':'#d38d48',.09,2);plank.position.set(0,3.84,z);picnic.add(plank);});
  [-3.4,3.4].forEach((x)=>[-.92,.92].forEach((z)=>{const bolt=cylinder(.09,.09,.05,'#5d6068',10);bolt.position.set(x,3.95,z);picnic.add(bolt);}));
  [-1,1].forEach(s=>{const bench=roundedBox(9,.42,1.2,'#d99a51',.15,3);bench.position.set(0,1.7,s*2.5);picnic.add(bench);const leg=roundedBox(.5,3.4,.5,'#755037',.1,2);leg.position.set(s*2.8,1.7,s*1.1);leg.rotation.z=s*.3;picnic.add(leg);});
  addAt(world.group,picnic,picnicX,0,picnicZ,.16);
  addLocalObstacle(world,picnicX,picnicZ,0,0,9.2,3.2,.16,'picnic-top',3.18,4.02);
  [-1,1].forEach((side)=>addLocalObstacle(world,picnicX,picnicZ,0,side*2.5,9.2,1.3,.16,'picnic-bench',1.45,1.95));
  [-1,1].forEach((side)=>addLocalObstacle(world,picnicX,picnicZ,side*2.8,side*1.1,.8,.8,.16,'picnic-leg',0,3.45));

  // Macetas y una rampa de tablón sobre una piedra.
  const potAX=-19*MAP_SIZE_MULTIPLIER;const potAZ=10*MAP_SIZE_MULTIPLIER;
  const potBX=20*MAP_SIZE_MULTIPLIER;const potBZ=-3*MAP_SIZE_MULTIPLIER;
  addPlant(world.group,potAX,potAZ,1.05);addCircleObstacle(world,potAX,potAZ,.9,'pot',0,1.05);
  addPlant(world.group,potBX,potBZ,.9);addCircleObstacle(world,potBX,potBZ,.8,'pot',0,.95);

  // Arenero para contraste de superficie.
  const sandboxX=-14*MAP_SIZE_MULTIPLIER;
  const sandboxZ=2*MAP_SIZE_MULTIPLIER;
  const sandbox=roundedBox(9,.7,7,'#dca64f',.22,3);sandbox.position.set(sandboxX,.35,sandboxZ);world.group.add(sandbox);
  const sand=roundedBox(8.1,.2,6.1,'#edce78',.18,3);sand.position.set(sandboxX,.75,sandboxZ);world.group.add(sand);addObstacle(world,sandboxX,sandboxZ,9.4,7.4,'sandbox',0,.9);
  const bucket=cylinder(.7,.5,1.1,'#e85d5d',12);bucket.position.set(sandboxX-1.5,1.35,sandboxZ+.3);world.group.add(bucket);
  addCircleObstacle(world,sandboxX-1.5,sandboxZ+.3,.68,'bucket',0,1.95);
  const bucketRim=new THREE.Mesh(new THREE.TorusGeometry(.69,.08,6,14),toon('#f5c84f'));bucketRim.rotation.x=Math.PI/2;bucketRim.position.set(sandboxX-1.5,1.9,sandboxZ+.3);world.group.add(bucketRim);
  const shovelBlade=roundedBox(.65,.1,.9,'#4d8ed6',.15,3);shovelBlade.position.set(sandboxX+1.5,1.0,sandboxZ-.6);shovelBlade.rotation.set(0,.4,-.2);world.group.add(shovelBlade);
  const shovelHandle=cylinder(.11,.11,2.2,'#f0c54d',9);shovelHandle.position.set(sandboxX+1.9,1.75,sandboxZ-.35);shovelHandle.rotation.z=-.45;world.group.add(shovelHandle);

  return world;
}

function createRuptureSky() {
  const root=new THREE.Group();
  root.name='rupture-toy-sky';
  const random=seededRandom(7741);

  const dome=new THREE.Mesh(
    new THREE.SphereGeometry(260,48,28),
    new THREE.ShaderMaterial({
      side:THREE.BackSide,
      depthWrite:false,
      fog:false,
      toneMapped:false,
      vertexShader:`
        varying float vSkyHeight;
        void main(){
          vSkyHeight=normalize(position).y*.5+.5;
          gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);
        }
      `,
      fragmentShader:`
        varying float vSkyHeight;
        void main(){
          vec3 lower=vec3(0.20,0.49,0.62);
          vec3 horizon=vec3(0.37,0.29,0.68);
          vec3 upper=vec3(0.055,0.028,0.16);
          float horizonBlend=smoothstep(0.08,0.52,vSkyHeight);
          float upperBlend=smoothstep(0.48,0.98,vSkyHeight);
          vec3 color=mix(lower,horizon,horizonBlend);
          color=mix(color,upper,upperBlend);
          float glow=1.0-abs(vSkyHeight-.43)*5.2;
          color+=vec3(.06,.11,.13)*max(0.0,glow);
          gl_FragColor=vec4(color,1.0);
        }
      `,
    }),
  );
  dome.name='rupture-sky-gradient';
  dome.position.y=12;
  dome.renderOrder=-1000;
  dome.frustumCulled=false;
  dome.userData.noShadow=true;
  root.add(dome);

  const starPositions=[];
  const starColors=[];
  const starPalette=[
    new THREE.Color('#d9fbff'),
    new THREE.Color('#fff3a6'),
    new THREE.Color('#d8c8ff'),
  ];
  for(let index=0;index<180;index+=1){
    const angle=random()*Math.PI*2;
    const radius=145+random()*85;
    const elevation=-.12+random()*1.02;
    starPositions.push(
      Math.cos(angle)*radius,
      -4+elevation*145,
      Math.sin(angle)*radius,
    );
    const color=starPalette[index%starPalette.length];
    starColors.push(color.r,color.g,color.b);
  }
  const starGeometry=new THREE.BufferGeometry();
  starGeometry.setAttribute('position',new THREE.Float32BufferAttribute(starPositions,3));
  starGeometry.setAttribute('color',new THREE.Float32BufferAttribute(starColors,3));
  const stars=new THREE.Points(
    starGeometry,
    new THREE.PointsMaterial({
      size:1.45,
      sizeAttenuation:true,
      vertexColors:true,
      transparent:true,
      opacity:.88,
      depthWrite:false,
      fog:false,
      toneMapped:false,
    }),
  );
  stars.name='rupture-toy-stars';
  stars.renderOrder=-900;
  root.add(stars);

  const auroras=new THREE.Group();
  auroras.name='rupture-auroras';
  ['#52f1e1','#9b75ff','#64d9ff'].forEach((color,index)=>{
    const geometry=new THREE.PlaneGeometry(118,18,32,2);
    const positions=geometry.attributes.position;
    for(let vertex=0;vertex<positions.count;vertex+=1){
      const x=positions.getX(vertex);
      const y=positions.getY(vertex);
      positions.setY(vertex,y+Math.sin(x*.075+index*1.8)*3.2);
    }
    positions.needsUpdate=true;
    const material=new THREE.MeshBasicMaterial({
      color,
      transparent:true,
      opacity:.10+index*.025,
      side:THREE.DoubleSide,
      depthWrite:false,
      fog:false,
      toneMapped:false,
    });
    const band=new THREE.Mesh(geometry,material);
    band.position.set(0,47+index*11,-142);
    band.rotation.z=(index-1)*.08;
    band.userData.noShadow=true;
    const pivot=new THREE.Group();
    pivot.rotation.y=index*Math.PI*2/3+.35;
    pivot.add(band);
    auroras.add(pivot);
  });
  root.add(auroras);

  const cloudGeometry=new THREE.IcosahedronGeometry(1,2);
  const cloudMaterials=['#d7f5ff','#cbdcff','#dfd1ff'].map((color)=>new THREE.MeshToonMaterial({
    color,
    transparent:true,
    opacity:.78,
    fog:false,
  }));
  const clouds=new THREE.Group();
  clouds.name='rupture-cloud-sea';
  for(let index=0;index<14;index+=1){
    const cloud=new THREE.Group();
    const angle=random()*Math.PI*2;
    const radius=24+random()*92;
    cloud.position.set(
      Math.cos(angle)*radius,
      -19-random()*20,
      Math.sin(angle)*radius,
    );
    const lobeCount=4+Math.floor(random()*3);
    for(let lobe=0;lobe<lobeCount;lobe+=1){
      const puff=new THREE.Mesh(cloudGeometry,cloudMaterials[(index+lobe)%cloudMaterials.length]);
      puff.position.set((lobe-(lobeCount-1)*.5)*5.2,(random()-.35)*2.2,(random()-.5)*5);
      puff.scale.set(6.5+random()*5,3.4+random()*3.6,5.8+random()*4.5);
      puff.userData.noShadow=true;
      cloud.add(puff);
    }
    cloud.userData.origin=cloud.position.clone();
    cloud.userData.phase=random()*Math.PI*2;
    cloud.userData.speed=.035+random()*.04;
    clouds.add(cloud);
  }
  root.add(clouds);

  const debris=new THREE.Group();
  debris.name='rupture-floating-toys';
  const debrisColors=['#ff6b55','#ffd83d','#42dce8','#8a68f4','#4ee69a'];
  for(let index=0;index<24;index+=1){
    const toy=new THREE.Group();
    const angle=random()*Math.PI*2;
    const radius=92+random()*50;
    toy.position.set(
      Math.cos(angle)*radius,
      -5+random()*76,
      Math.sin(angle)*radius,
    );
    const color=debrisColors[index%debrisColors.length];
    const body=new THREE.Mesh(
      new THREE.BoxGeometry(3.6+random()*4,2.4+random()*3.2,3.4+random()*4),
      new THREE.MeshToonMaterial({color,fog:false}),
    );
    body.userData.noShadow=true;
    const stud=new THREE.Mesh(
      new THREE.CylinderGeometry(.65, .72, .7, 12),
      new THREE.MeshToonMaterial({color,fog:false}),
    );
    stud.position.y=body.geometry.parameters.height*.5+.34;
    stud.userData.noShadow=true;
    toy.add(body,stud);
    toy.rotation.set(random()*Math.PI,random()*Math.PI,random()*Math.PI);
    toy.userData.spin=new THREE.Vector3(
      (random()-.5)*.16,
      (random()-.5)*.22,
      (random()-.5)*.16,
    );
    toy.userData.bob=random()*Math.PI*2;
    toy.userData.originY=toy.position.y;
    debris.add(toy);
  }
  root.add(debris);

  const moon=new THREE.Group();
  moon.name='rupture-toy-moon';
  const moonBall=new THREE.Mesh(
    new THREE.SphereGeometry(11,24,16),
    new THREE.MeshToonMaterial({color:'#fff0a6',fog:false}),
  );
  moonBall.scale.set(1,1,.88);
  moonBall.userData.noShadow=true;
  const moonBand=new THREE.Mesh(
    new THREE.TorusGeometry(11.15,.72,10,42),
    new THREE.MeshToonMaterial({color:'#ff7893',fog:false}),
  );
  moonBand.rotation.x=Math.PI/2;
  moonBand.userData.noShadow=true;
  const moonRing=new THREE.Mesh(
    new THREE.TorusGeometry(16,.55,10,58),
    new THREE.MeshBasicMaterial({
      color:'#6de7ef',
      transparent:true,
      opacity:.78,
      depthWrite:false,
      fog:false,
      toneMapped:false,
    }),
  );
  moonRing.rotation.set(Math.PI/2.55,.22,-.35);
  moonRing.scale.y=.42;
  moonRing.userData.noShadow=true;
  moon.add(moonBall,moonBand,moonRing);
  moon.position.set(-78,62,-128);
  root.add(moon);

  root.userData.time=0;
  root.userData.quality='high';
  root.userData.updateAccumulator=0;
  root.userData.lastVisualDt=0;
  root.userData.setQuality=(quality='high')=>{
    const profile={
      low:{stars:60,auroras:0,clouds:6,debris:6,speed:.78,interval:1/30},
      medium:{stars:120,auroras:2,clouds:10,debris:13,speed:1,interval:0},
      high:{stars:180,auroras:3,clouds:14,debris:24,speed:1.35,interval:0},
    }[quality]||null;
    const resolved=profile?quality:'high';
    const settings=profile||{
      stars:180,auroras:3,clouds:14,debris:24,speed:1.35,interval:0,
    };
    root.userData.quality=resolved;
    root.userData.qualityProfile=settings;
    starGeometry.setDrawRange(0,settings.stars);
    auroras.children.forEach((child,index)=>{child.visible=index<settings.auroras;});
    clouds.children.forEach((child,index)=>{child.visible=index<settings.clouds;});
    debris.children.forEach((child,index)=>{child.visible=index<settings.debris;});
  };
  root.userData.setQuality('high');
  root.userData.update=(dt,cameraPosition=null)=>{
    // El domo siempre sigue a la cámara, aun cuando reducimos la frecuencia
    // de las animaciones decorativas en calidad Baja.
    if(cameraPosition)dome.position.copy(cameraPosition);
    root.userData.updateAccumulator+=dt;
    const interval=root.userData.qualityProfile?.interval||0;
    if(interval&&root.userData.updateAccumulator<interval)return false;
    const visualDt=interval?root.userData.updateAccumulator:dt;
    root.userData.updateAccumulator=0;
    root.userData.lastVisualDt=visualDt;
    const speed=root.userData.qualityProfile?.speed||1;
    root.userData.time+=visualDt*speed;
    const time=root.userData.time;
    // El domo debe permanecer centrado en la cámara. Si queda anclado al origen,
    // al avanzar por el recorrido su cara más lejana cruza el far plane y el
    // recorte se percibe como un círculo oscuro que sigue la dirección de vista.
    stars.rotation.y+=visualDt*.004*speed;
    auroras.children.forEach((pivot,index)=>{
      if(!pivot.visible)return;
      pivot.rotation.y+=visualDt*(index%2?.0025:-.002)*speed;
      const band=pivot.children[0];
      band.material.opacity=.09+index*.022+Math.sin(time*.45+index)*.025;
    });
    clouds.children.forEach((cloud)=>{
      if(!cloud.visible)return;
      const origin=cloud.userData.origin;
      const phase=cloud.userData.phase;
      cloud.position.x=origin.x+Math.sin(time*cloud.userData.speed+phase)*4.5;
      cloud.position.z=origin.z+Math.cos(time*cloud.userData.speed*.8+phase)*3.2;
      cloud.position.y=origin.y+Math.sin(time*.12+phase)*1.1;
    });
    debris.children.forEach((toy)=>{
      if(!toy.visible)return;
      toy.rotation.x+=toy.userData.spin.x*visualDt*speed;
      toy.rotation.y+=toy.userData.spin.y*visualDt*speed;
      toy.rotation.z+=toy.userData.spin.z*visualDt*speed;
      toy.position.y=toy.userData.originY+Math.sin(time*.42+toy.userData.bob)*1.4;
    });
    moon.rotation.y+=visualDt*.025*speed;
    moonRing.rotation.z+=visualDt*.018*speed;
    return true;
  };
  return root;
}

function createRuptureStormSky() {
  const root=createRuptureSky();
  root.name='rupture-storm-sky';

  const dome=root.getObjectByName('rupture-sky-gradient');
  if(dome?.material){
    dome.material=dome.material.clone();
    dome.material.fragmentShader=dome.material.fragmentShader
      .replace('vec3 lower=vec3(0.20,0.49,0.62);','vec3 lower=vec3(0.055,0.23,0.25);')
      .replace('vec3 horizon=vec3(0.37,0.29,0.68);','vec3 horizon=vec3(0.11,0.35,0.42);')
      .replace('vec3 upper=vec3(0.055,0.028,0.16);','vec3 upper=vec3(0.018,0.035,0.10);')
      .replace('color+=vec3(.06,.11,.13)*max(0.0,glow);','color+=vec3(.12,.16,.05)*max(0.0,glow);');
    dome.material.needsUpdate=true;
  }

  const cloudPalette=['#456a78','#58667f','#3c596b'];
  root.getObjectByName('rupture-cloud-sea')?.children.forEach((cloud,cloudIndex)=>{
    cloud.children.forEach((puff,puffIndex)=>{
      puff.material=puff.material.clone();
      puff.material.color.set(cloudPalette[(cloudIndex+puffIndex)%cloudPalette.length]);
      puff.material.opacity=.82;
    });
  });
  const auroraPalette=['#62ffe4','#ffe35a','#48cfff'];
  root.getObjectByName('rupture-auroras')?.children.forEach((pivot,index)=>{
    pivot.children[0]?.material?.color.set(auroraPalette[index%auroraPalette.length]);
  });

  const stormClouds=new THREE.Group();
  stormClouds.name='rupture-storm-clouds';
  const cloudGeometry=new THREE.IcosahedronGeometry(1,2);
  const stormRandom=seededRandom(3097);
  for(let index=0;index<10;index+=1){
    const cluster=new THREE.Group();
    const angle=index/10*Math.PI*2+.18;
    const radius=94+stormRandom()*42;
    cluster.position.set(
      Math.cos(angle)*radius,
      29+stormRandom()*42,
      Math.sin(angle)*radius,
    );
    for(let lobe=0;lobe<5;lobe+=1){
      const puff=new THREE.Mesh(
        cloudGeometry,
        new THREE.MeshToonMaterial({
          color:cloudPalette[(index+lobe)%cloudPalette.length],
          transparent:true,
          opacity:.9,
          fog:false,
        }),
      );
      puff.position.set((lobe-2)*5.5,(stormRandom()-.5)*3,(stormRandom()-.5)*6);
      puff.scale.set(7+stormRandom()*5,3.6+stormRandom()*3,6+stormRandom()*4);
      puff.userData.noShadow=true;
      cluster.add(puff);
    }
    cluster.userData.phase=stormRandom()*Math.PI*2;
    cluster.userData.originY=cluster.position.y;
    stormClouds.add(cluster);
  }
  root.add(stormClouds);

  const lightning=new THREE.Group();
  lightning.name='rupture-storm-lightning';
  [
    [-84,68,-76],
    [92,75,-38],
    [-104,59,54],
    [88,64,91],
    [12,86,-126],
  ].forEach(([x,y,z],index)=>{
    const points=[new THREE.Vector3(0,0,0)];
    for(let segment=1;segment<=7;segment+=1){
      points.push(new THREE.Vector3(
        (segment%2?1:-1)*(1.2+(index%3)*.5),
        -segment*4.2,
        (segment%3-1)*.8,
      ));
    }
    const bolt=new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(points),
      new THREE.LineBasicMaterial({
        color:index%2?'#7efeff':'#fff36a',
        transparent:true,
        opacity:.85,
        depthWrite:false,
        fog:false,
        toneMapped:false,
      }),
    );
    bolt.position.set(x,y,z);
    bolt.userData.phase=index*1.37;
    bolt.userData.noShadow=true;
    lightning.add(bolt);
  });
  root.add(lightning);

  const turbine=new THREE.Group();
  turbine.name='rupture-storm-turbine';
  const turbineRing=new THREE.Mesh(
    new THREE.TorusGeometry(18,1.25,12,48),
    new THREE.MeshToonMaterial({color:'#54dbea',fog:false}),
  );
  turbineRing.userData.noShadow=true;
  const rotor=new THREE.Group();
  rotor.name='rupture-storm-rotor';
  for(let index=0;index<4;index+=1){
    const blade=roundedBox(4.2,15,1.4,index%2?'#ffd94e':'#f6f9ff',.45,3);
    blade.position.y=8.4;
    blade.rotation.z=index*Math.PI*.5;
    blade.userData.noShadow=true;
    rotor.add(blade);
  }
  const hub=new THREE.Mesh(
    new THREE.SphereGeometry(3.7,20,14),
    new THREE.MeshToonMaterial({color:'#ff6d79',fog:false}),
  );
  hub.scale.z=.65;
  hub.userData.noShadow=true;
  rotor.add(hub);
  turbine.add(turbineRing,rotor);
  turbine.position.set(-102,62,-118);
  turbine.rotation.y=-.52;
  root.add(turbine);

  const fallingToys=new THREE.Group();
  fallingToys.name='rupture-storm-falling-toys';
  const fallingPalette=['#ff6d79','#ffd94e','#52e2ef','#8f78ff','#56e89c','#ff9048'];
  const routeCenters=[
    [0,0],[-42,30],[-7,70],[42,42],[53,-14],[11,-52],
  ];
  for(let index=0;index<48;index+=1){
    const toy=new THREE.Group();
    const color=fallingPalette[index%fallingPalette.length];
    const accent=fallingPalette[(index+2)%fallingPalette.length];
    if(index%4===0){
      const body=roundedBox(4.4,3.2,3.8,color,.42,3);
      body.position.y=-.2;
      toy.add(body);
      [-1.2,1.2].forEach((x)=>{
        const stud=cylinder(.58,.66,.7,accent,12);
        stud.position.set(x,1.72,0);
        toy.add(stud);
      });
    }else if(index%4===1){
      const ball=sphere(2.55,color,18,12);
      const band=new THREE.Mesh(new THREE.TorusGeometry(2.57,.22,8,28),toon(accent));
      band.rotation.x=Math.PI/2;
      toy.add(ball,band);
    }else if(index%4===2){
      const top=new THREE.Mesh(new THREE.ConeGeometry(2.8,4.4,16),toon(color));
      top.rotation.z=Math.PI;
      const ring=new THREE.Mesh(new THREE.TorusGeometry(2.05,.28,8,24),toon(accent));
      ring.rotation.x=Math.PI/2;
      ring.position.y=.5;
      const handle=cylinder(.32,.32,2.1,accent,10);
      handle.position.y=3.05;
      toy.add(top,ring,handle);
    }else{
      const body=roundedBox(5.6,1.7,2.2,color,.38,3);
      const wing=roundedBox(2.3,.5,7.5,accent,.2,2);
      const tail=roundedBox(1.7,2.5,.5,accent,.2,2);
      tail.position.set(-2.3,1.35,0);
      const nose=sphere(1.05,'#f6fbff',14,10);
      nose.position.x=3;
      toy.add(body,wing,tail,nose);
    }
    toy.scale.setScalar(1.25+stormRandom()*.42);
    const angle=index/48*Math.PI*2+stormRandom()*.16;
    let radius=84+stormRandom()*42;
    let x=Math.cos(angle)*radius;
    let z=Math.sin(angle)*radius;
    while(routeCenters.some(([routeX,routeZ])=>
      Math.hypot(x-routeX,z-routeZ)<34
    )){
      radius+=14;
      x=Math.cos(angle)*radius;
      z=Math.sin(angle)*radius;
    }
    toy.position.set(x,-45+stormRandom()*165,z);
    toy.rotation.set(
      stormRandom()*Math.PI,
      stormRandom()*Math.PI,
      stormRandom()*Math.PI,
    );
    toy.userData.fallSpeed=7+stormRandom()*8;
    toy.userData.spin=new THREE.Vector3(
      (stormRandom()-.5)*1.1,
      (stormRandom()-.5)*1.35,
      (stormRandom()-.5)*1.1,
    );
    toy.userData.phase=stormRandom()*Math.PI*2;
    toy.userData.originX=x;
    toy.userData.originZ=z;
    toy.traverse((child)=>{child.userData.noShadow=true;});
    fallingToys.add(toy);
  }
  root.add(fallingToys);

  const baseUpdate=root.userData.update;
  const baseSetQuality=root.userData.setQuality;
  root.userData.setQuality=(quality='high')=>{
    baseSetQuality?.(quality);
    const profile={
      low:{stormClouds:4,lightning:2,fallingToys:10},
      medium:{stormClouds:7,lightning:3,fallingToys:24},
      high:{stormClouds:10,lightning:5,fallingToys:48},
    }[quality]||{stormClouds:10,lightning:5,fallingToys:48};
    stormClouds.children.forEach((child,index)=>{child.visible=index<profile.stormClouds;});
    lightning.children.forEach((child,index)=>{child.visible=index<profile.lightning;});
    fallingToys.children.forEach((child,index)=>{child.visible=index<profile.fallingToys;});
  };
  root.userData.setQuality(root.userData.quality);
  root.userData.stormTime=0;
  root.userData.update=(dt,cameraPosition=null)=>{
    if(baseUpdate?.(dt,cameraPosition)===false)return false;
    const visualDt=root.userData.lastVisualDt||dt;
    const speed=root.userData.qualityProfile?.speed||1;
    root.userData.stormTime+=visualDt*speed;
    const time=root.userData.stormTime;
    stormClouds.children.forEach((cluster,index)=>{
      if(!cluster.visible)return;
      cluster.position.y=cluster.userData.originY+Math.sin(time*.18+cluster.userData.phase)*1.8;
      cluster.rotation.y+=visualDt*(index%2?.012:-.009)*speed;
    });
    lightning.children.forEach((bolt,index)=>{
      if(!bolt.visible)return;
      const flash=Math.sin(time*(7.5+index*.65)+bolt.userData.phase);
      bolt.material.opacity=flash>.78?.95:.08;
    });
    fallingToys.children.forEach((toy)=>{
      if(!toy.visible)return;
      toy.position.y-=toy.userData.fallSpeed*visualDt*speed;
      toy.position.x=toy.userData.originX+Math.sin(time*.42+toy.userData.phase)*2.2;
      toy.position.z=toy.userData.originZ+Math.cos(time*.35+toy.userData.phase)*1.7;
      toy.rotation.x+=toy.userData.spin.x*visualDt*speed;
      toy.rotation.y+=toy.userData.spin.y*visualDt*speed;
      toy.rotation.z+=toy.userData.spin.z*visualDt*speed;
      if(toy.position.y<-62)toy.position.y=105+(toy.userData.phase/Math.PI)*18;
    });
    rotor.rotation.z-=visualDt*.62*speed;
    return true;
  };
  return root;
}

function createRuptureVoid() {
  const group=new THREE.Group();
  group.name='rupture-void-map';
  const sky=createRuptureSky();
  group.add(sky);
  return {
    id:'rupture-void',
    group,
    // Los límites físicos quedan muy lejos del recorrido. No hay piso, paredes
    // ni decoración debajo de las plataformas construidas por RuptureArena.
    width:150,
    depth:180,
    shadowSpan:72,
    groundHeight:-120,
    obstacles:[],
    ramps:[],
    spawn:new THREE.Vector3(0,14.025,0),
    showcase:new THREE.Vector3(0,14.025,0),
    spawnYaw:0,
    colors:{floor:'#171331',map:'#7555c9',line:'#fff16a'},
    update:(dt,camera)=>sky.userData.update?.(dt,camera?.position),
    setGraphicsQuality:(quality)=>sky.userData.setQuality?.(quality),
  };
}

function createRuptureStorm() {
  const group=new THREE.Group();
  group.name='rupture-storm-map';
  const sky=createRuptureStormSky();
  group.add(sky);
  return {
    id:'rupture-storm',
    group,
    width:180,
    depth:200,
    shadowSpan:76,
    groundHeight:-130,
    obstacles:[],
    ramps:[],
    spawn:new THREE.Vector3(0,13.025,0),
    showcase:new THREE.Vector3(0,13.025,0),
    spawnYaw:0,
    colors:{floor:'#102b32',map:'#28c8c7',line:'#fff36a'},
    update:(dt,camera)=>sky.userData.update?.(dt,camera?.position),
    setGraphicsQuality:(quality)=>sky.userData.setQuality?.(quality),
  };
}

function createProceduralMap(mapId) {
  if (mapId === 'rupture-void') return createRuptureVoid();
  if (mapId === 'rupture-storm') return createRuptureStorm();
  if (mapId === 'bedroom') return createBedroom();
  if (mapId === 'garden') return createGarden();
  return createLiving();
}

export function createMap(mapId,{baseOnly=false}={}) {
  const world=createProceduralMap(mapId);
  if(!baseOnly&&collisionReleases[world.id])applyMapDocument(world,collisionReleases[world.id]);
  return world;
}

export function getGroundHeight(world, position) {
  let height = world.groundHeight??0;
  for (const ramp of world.ramps) {
    const dx = position.x - ramp.x;
    const dz = position.z - ramp.z;
    const cos = Math.cos(-ramp.rotationY);
    const sin = Math.sin(-ramp.rotationY);
    const localX = dx * cos - dz * sin;
    const localZ = dx * sin + dz * cos;
    if (Math.abs(localX) <= ramp.width / 2 && Math.abs(localZ) <= ramp.depth / 2) {
      const t = THREE.MathUtils.clamp((ramp.depth / 2 - localZ) / ramp.depth, 0, 1);
      height = Math.max(height, ramp.height * t);
    }
  }
  return height;
}
